/**
 * Pet Recommendation Quiz Component
 * 
 * Web component that handles quiz navigation, data collection, and Gadget API submission
 * Reuses navigation patterns from feeding-calculator.js
 */

// Example data format sent to Gadget API
// {
//   "shop": { "_link": "shop_id" },
//   "email": "customer@example.com",
//   "userName": "John Doe",
//   "termsChecked": true,
//   "petName": "Chloe",
//   "petType": "dog",
//   "petIndex": 1,
//   "petBreed": "Andalusian Podenco",
//   "petAllergies": "[\"salmon\"]",
//   "petDietChangeReason": "Their dietary needs have changed due to sensitivities",
//   "petGender": "female",
//   "petBirthday": "2025-01-05",
//   "isPetRescue": true,
//   "petWeight": 12,
//   "petActivityLevel": "moderate_activity",
//   "petBodyType": "medium",
//   "petFoodType": "wet",
//   "wetFoodRatio": 100,
//   "dryFoodRatio": 0,
//   "petBrandFood": "Naturediet",
//   "petEatsTreats": true,
//   "petPooType": "Perfect Poop!"
// }

if (!customElements.get('pet-quiz')) {
  customElements.define(
    'pet-quiz',
    class PetQuiz extends HTMLElement {
      constructor() {
        super();
        this.slides = [];
        this.currentSlideIndex = 0;
        this.sectionId = this.dataset.sectionId;
        this.shopId = this.dataset.shopId;
        this.debugMode = this.dataset.debugMode === 'true';
        this.isDesignMode = typeof Shopify !== 'undefined' && Shopify.designMode;
        
        // State management
        this.quizData = {};
        this.navigationHistory = [];
        this.storageKey = `pet-quiz-${this.sectionId}`;
        this.gadgetInitialized = false;
        
        // Bind methods for event listeners
        this.boundHandleInputChange = this.handleInputChange.bind(this);
        this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
      }

      connectedCallback() {
        this.initSlides();
        this.setupEventListeners();
        this.initProgressBar();
        this.setupResizeObserver();
        this.setupGadgetListener();
        this.setupDummyDataButton();
        
        if (this.isDesignMode) {
          this.setupDesignMode();
        } else {
          // Not in design mode - normal behavior
          this.restoreFromLocalStorage();
          
          if (this.slides.length > 0 && this.navigationHistory.length === 0) {
            const firstSlide = this.slides[0];
            this.ensureDefaultSelections(firstSlide);
            this.addToHistory(firstSlide, 'initial');
            firstSlide.classList.add('pet-quiz-slide--active', 'feeding-calculator-slide--active');
            this.currentSlideIndex = 0;
            this.updateProgressBar();
          // Collect quiz data to capture default values (e.g., checked checkboxes)
          // But don't persist food type if it wasn't explicitly selected
          this.collectQuizData();
          // Clear food type data if it was set by default (not user-selected)
          if (this.quizData.petFoodType && !this.querySelector('[name="pet_food_type"]')?.value) {
            delete this.quizData.petFoodType;
            delete this.quizData.wetFoodRatio;
            delete this.quizData.dryFoodRatio;
            delete this.quizData.petFoodMixRatio;
          }
          this.persistToLocalStorage();
          } else if (this.navigationHistory.length > 0) {
            const lastHistoryEntry = this.navigationHistory[this.navigationHistory.length - 1];
            const slide = this.findSlideById(lastHistoryEntry.slideId);
            if (slide) {
              const slideIndex = parseInt(slide.dataset.slideIndex, 10);
              this.showSlide(slideIndex, true);
              this.restoreFormData();
            }
          }
        }
      }

      disconnectedCallback() {
        this.removeEventListeners();
        if (this.isDesignMode && this.boundHandleBlockSelect && this.boundHandleSectionLoad) {
          document.removeEventListener('shopify:block:select', this.boundHandleBlockSelect);
          document.removeEventListener('shopify:section:load', this.boundHandleSectionLoad);
        }
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
      }

      setupGadgetListener() {
        // Listen for Gadget initialization
        const handleGadgetInit = (event) => {
          if (event.detail && event.detail.success) {
            this.gadgetInitialized = true;
            if (this.debugMode) {
              console.info('Pet Quiz: Gadget API ready');
            }
          }
        };
        
        window.addEventListener('gadgetInitialized', handleGadgetInit);
        this.boundHandleGadgetInit = handleGadgetInit;
        
        // Check if already initialized
        if (window.api && window.api.quizResponse) {
          this.gadgetInitialized = true;
        }
      }

      setupResizeObserver() {
        if (typeof ResizeObserver === 'undefined') return;
        
        this.resizeObserver = new ResizeObserver(() => {
          const activeSlide = this.slides[this.currentSlideIndex];
          if (activeSlide && activeSlide.classList.contains('pet-quiz-slide--active')) {
            this.updateWrapperHeight();
          }
        });
        
        this.slides.forEach(slide => {
          this.resizeObserver.observe(slide);
        });
      }

      initSlides() {
        // Find all quiz slides (both pet-quiz-slide and feeding-calculator-slide classes)
        this.slides = Array.from(this.querySelectorAll('.pet-quiz-slide, .feeding-calculator-slide'));
        
        if (this.slides.length === 0) return;

        this.slides.forEach((slide, index) => {
          const slideId = slide.id || `slide-${index}`;
          const blockId = slide.dataset.slideId || slide.id.replace('slide-', '');
          
          slide.dataset.slideIndex = index;
          slide.dataset.slideNumber = index;
          if (!slide.id) {
            slide.id = slideId;
          }
          
          // Store slide type
          if (slide.classList.contains('pet-quiz-slide--user-info')) {
            slide.dataset.slideType = 'user-info';
          } else if (slide.classList.contains('pet-quiz-slide--pet-details')) {
            slide.dataset.slideType = 'pet-details';
          } else if (slide.classList.contains('pet-quiz-slide--food-preference')) {
            slide.dataset.slideType = 'food-preference';
          } else if (slide.classList.contains('pet-quiz-slide--allergies')) {
            slide.dataset.slideType = 'allergies';
          } else if (slide.classList.contains('pet-quiz-slide--poo-type')) {
            slide.dataset.slideType = 'poo-type';
          } else if (slide.classList.contains('pet-quiz-slide--confirmation')) {
            slide.dataset.slideType = 'confirmation';
          } else if (slide.classList.contains('feeding-calculator-slide--cat')) {
            slide.dataset.slideType = 'cat';
          } else if (slide.classList.contains('feeding-calculator-slide--dog')) {
            slide.dataset.slideType = 'dog';
          } else if (slide.classList.contains('feeding-calculator-slide--buttons')) {
            slide.dataset.slideType = 'buttons';
          } else {
            slide.dataset.slideType = slide.dataset.slideType || 'unknown';
          }
        });

        // Initialize slide-specific features
        this.initBreedSelectors();
      }

      initProgressBar() {
        // Progress bar can be in pet-quiz-main (section) or parent block
        const quizMain = this.closest('.pet-quiz-main');
        if (!quizMain) return;
        
        this.progressBar = quizMain.querySelector('[data-progress-bar]');
        this.progressBarFill = quizMain.querySelector('[data-progress-bar-fill]');
        this.progressBarText = quizMain.querySelector('[data-progress-bar-text]');
      }

      updateWrapperHeight() {
        const slidesWrapper = this.querySelector('.pet-quiz-main__slides-wrapper');
        if (!slidesWrapper) return;

        const activeSlide = this.slides[this.currentSlideIndex];
        if (!activeSlide) return;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!activeSlide.classList.contains('pet-quiz-slide--active')) {
              return;
            }

            void activeSlide.offsetHeight;
            const slideHeight = activeSlide.scrollHeight;
            
            if (slideHeight > 0) {
              slidesWrapper.style.height = `${slideHeight}px`;
            } else {
              slidesWrapper.style.height = '';
            }
          });
        });
      }

      updateProgressBar() {
        if (!this.progressBar || !this.progressBarFill) {
          // Re-initialize if not found (might be loaded after DOM ready)
          this.initProgressBar();
          if (!this.progressBar || !this.progressBarFill) return;
        }

        const progressSlides = this.getProgressSlides();
        const totalSlides = progressSlides.length;
        if (totalSlides === 0) return;

        const currentSlide = this.slides[this.currentSlideIndex];
        let currentStep = progressSlides.findIndex((slide) => slide === currentSlide) + 1;
        if (currentStep <= 0) {
          // Fallback for edge-cases where the current slide is temporarily out of filtered set.
          currentStep = Math.min(this.currentSlideIndex + 1, totalSlides);
        }

        // Calculate progress based on current slide index within filtered set
        const progress = (currentStep / totalSlides) * 100;
        const progressPercentage = Math.min(100, Math.max(0, progress));
        
        // Ensure the fill element is visible
        this.progressBarFill.style.width = `${progressPercentage}%`;
        this.progressBarFill.style.display = 'block';
        
        if (this.progressBarText) {
          this.progressBarText.textContent = `${currentStep} / ${totalSlides}`;
        }
      }

      getCurrentPetType() {
        const petType = this.currentPetType || this.quizData.pet_type || this.quizData.petType;
        return petType ? petType.toLowerCase().trim() : '';
      }

      getSlideProgressKey(slide) {
        if (!slide) return '';

        const identifier = (slide.dataset.slideIdentifier || '').toLowerCase().trim();
        if (identifier) {
          return identifier.replace(/-(cat|dog)$/, '');
        }

        if (slide.classList.contains('feeding-calculator-slide--cat')) return 'pet-type';
        if (slide.classList.contains('feeding-calculator-slide--dog')) return 'pet-type';

        return slide.id || slide.dataset.slideId || '';
      }

      getSlidePetType(slide) {
        if (!slide) return '';

        if (slide.classList.contains('feeding-calculator-slide--cat')) return 'cat';
        if (slide.classList.contains('feeding-calculator-slide--dog')) return 'dog';

        const identifier = (slide.dataset.slideIdentifier || '').toLowerCase().trim();
        if (identifier.endsWith('-cat')) return 'cat';
        if (identifier.endsWith('-dog')) return 'dog';

        return '';
      }

      getProgressSlides() {
        if (!Array.isArray(this.slides) || this.slides.length === 0) return [];

        const petType = this.getCurrentPetType();
        const filteredSlides = this.slides.filter((slide) => {
          const slidePetType = this.getSlidePetType(slide);
          if (!slidePetType) return true;
          return !petType || slidePetType === petType;
        });

        // If pet type isn't selected yet, collapse cat/dog duplicate steps into one.
        if (!petType) {
          const seenKeys = new Set();
          return filteredSlides.filter((slide) => {
            const key = this.getSlideProgressKey(slide);
            if (!key) return true;
            if (seenKeys.has(key)) return false;
            seenKeys.add(key);
            return true;
          });
        }

        return filteredSlides;
      }

      setupEventListeners() {
        if (this.isDesignMode) return;
        
        // Handle button clicks (navigation buttons, etc.)
        this.addEventListener('click', this.handleButtonClick.bind(this));
        
        // Handle input changes and allergy tag clicks
        this.addEventListener('click', (event) => {
          const target = event.target;
          // Handle option tag clicks - check if clicked element is button or child of button
          const optionTag = target.closest('.pet-quiz-slide__option-tag');
          if (optionTag) {
            event.preventDefault();
            event.stopPropagation();
            this.toggleOptionTag(optionTag);
            this.collectQuizData();
            this.persistToLocalStorage();
            return;
          }
          // Handle other input changes
          this.boundHandleInputChange(event);
        });
        
        this.addEventListener('input', this.boundHandleInputChange);
        this.addEventListener('change', this.boundHandleInputChange);
        this.addEventListener('submit', this.boundHandleFormSubmit);
        
        // Add real-time email validation on blur
        this.addEventListener('blur', (event) => {
          const field = event.target;
          if (field && field.type === 'email' && field.hasAttribute('data-quiz-field') && field.getAttribute('data-quiz-field') === 'email') {
            this.validateEmailField(field);
          }
        }, true); // Use capture phase to catch blur events
        
        // Add real-time date validation on blur
        this.addEventListener('blur', (event) => {
          const field = event.target;
          if (field && field.type === 'date' && field.hasAttribute('data-quiz-field') && field.getAttribute('data-quiz-field') === 'petDateOfBirth') {
            this.validateDateField(field);
          }
        }, true); // Use capture phase to catch blur events
        
        // Handle "unsure of breed" checkbox
        this.addEventListener('change', (event) => {
          const checkbox = event.target;
          if (checkbox.name === 'unsure_breed' && checkbox.hasAttribute('data-breed-block-id')) {
            this.handleUnsureBreedCheckbox(checkbox);
          }
        });
        
        // Handle quiz submission
        this.addEventListener('click', this.handleQuizSubmit.bind(this));
        
        // Variable replacement is handled synchronously in showSlide() before slide becomes visible
      }

      removeEventListeners() {
        this.removeEventListener('click', this.handleButtonClick.bind(this));
        this.removeEventListener('input', this.boundHandleInputChange);
        this.removeEventListener('change', this.boundHandleInputChange);
        this.removeEventListener('submit', this.boundHandleFormSubmit);
        if (this.boundHandleGadgetInit) {
          window.removeEventListener('gadgetInitialized', this.boundHandleGadgetInit);
        }
      }

      setupDesignMode() {
        // In design mode, show first slide by default
        if (this.slides.length > 0) {
          this.showSlide(0, true); // true = design mode, no transition
        }

        // Bind handlers for proper cleanup
        this.boundHandleBlockSelect = this.handleBlockSelect.bind(this);
        this.boundHandleSectionLoad = this.handleSectionLoad.bind(this);

        // Listen for block selection events
        document.addEventListener('shopify:block:select', this.boundHandleBlockSelect);
        document.addEventListener('shopify:section:load', this.boundHandleSectionLoad);
      }

      handleBlockSelect(event) {
        // Only handle events for this section
        if (event.detail.sectionId !== this.sectionId) return;

        const blockId = event.detail.blockId;
        
        // Find the slide that matches the selected block ID
        const slide = this.slides.find(s => {
          const slideBlockId = s.dataset.slideId || s.id.replace('slide-', '');
          return slideBlockId === blockId || s.id === `slide-${blockId}`;
        });
        
        if (slide) {
          const slideIndex = parseInt(slide.dataset.slideIndex, 10);
          // Show the selected slide
          this.showSlide(slideIndex, true); // true = design mode, no transition
        }
      }

      handleSectionLoad(event) {
        // Reinitialize when section is reloaded in design mode
        if (event.detail.sectionId === this.sectionId) {
          this.initSlides();
          this.setupDesignMode();
        }
      }

      handleInputChange(event) {
        const previousPetName = this.quizData.petName || this.quizData.pet_name || '';
        const previousPetType = this.quizData.petType || this.quizData.pet_type || '';
        this.collectQuizData();
        this.persistToLocalStorage();
        
        const currentPetName = this.quizData.petName || this.quizData.pet_name || '';
        const currentPetType = this.quizData.petType || this.quizData.pet_type || '';
        
        // If pet name or pet type was updated, replace variables in current slide
        if ((currentPetName && currentPetName !== previousPetName) || 
            (currentPetType && currentPetType !== previousPetType)) {
          // Small delay to ensure DOM is updated
          setTimeout(() => {
            this.replaceVariables();
          }, 50);
        }
        
        // Handle breed selector updates when pet type changes
        const target = event.target;
        if (target && (target.hasAttribute('data-pet-type') || target.hasAttribute('data-pet-type-field'))) {
          // Update all breed selectors when pet type changes
          const breedSelects = this.querySelectorAll('[data-quiz-field="petBreed"]');
          breedSelects.forEach(select => {
            this.updateBreedOptions(select);
          });
          
          // Update all brand search inputs when pet type changes
          const brandInputs = this.querySelectorAll('[data-quiz-field="petBrandFood"][type="text"]');
          brandInputs.forEach(input => {
            const petType = this.currentPetType || this.quizData.pet_type || this.quizData.petType;
            this.initBrandSearch(input, petType);
          });
        } else if (target && target.hasAttribute('data-pet-type-field')) {
          this.updateBreedOptions(target);
        }
        
        // Handle option tag clicks - check if clicked element is button or child of button
        const optionTag = target && target.closest('.pet-quiz-slide__option-tag');
        if (optionTag) {
          this.toggleOptionTag(optionTag);
        }
        
      }

      handleFormSubmit(event) {
        event.preventDefault();
        this.collectQuizData();
        this.persistToLocalStorage();
      }

      async handleButtonClick(event) {
        // Skip option tag buttons - they're handled separately
        if (event.target.classList.contains('pet-quiz-slide__option-tag')) {
          return;
        }
        
        let button = event.target.closest('button, a.btn, .btn, .feeding-calculator-slide__button, .feeding-calculator-slide__nav-button');
        
        if (!button) {
          const target = event.target;
          if (target.hasAttribute('data-next-slide') || target.hasAttribute('data-prev-slide')) {
            button = target;
          }
        }
        
        // Skip if it's an option tag button
        if (button && button.classList.contains('pet-quiz-slide__option-tag')) {
          return;
        }
        
        if (!button) return;

        const calculator = button.closest('pet-quiz');
        if (calculator !== this) return;

        const slide = button.closest('.pet-quiz-slide, .feeding-calculator-slide');
        if (!slide) return;

        // Handle back navigation
        const prevSlideTarget = button.getAttribute('data-prev-slide') || button.dataset.prevSlide;
        if (prevSlideTarget) {
          event.preventDefault();
          event.stopPropagation();
          
          const targetSlide = this.findSlide(prevSlideTarget);
          if (targetSlide) {
            const slideIndex = parseInt(targetSlide.dataset.slideIndex, 10);
            this.navigateToSlide(slideIndex);
          }
          return;
        }

        // Collect form data before navigation
        this.collectQuizData();

        // Handle next navigation
        const nextSlideTarget = button.getAttribute('data-next-slide') || button.dataset.nextSlide;
        if (nextSlideTarget) {
          // Validate before proceeding
          if (!this.validateSlide(slide)) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          
          // Check if we're leaving the user-info slide and have an email
          const isUserInfoSlide = slide.classList.contains('pet-quiz-slide--user-info') || 
                                 slide.dataset.slideIdentifier === 'user-info';
          if (isUserInfoSlide && this.quizData.email) {
            // Only call getPetIndex if petIndex is not already set
            if (this.quizData.petIndex === undefined || this.quizData.petIndex === null || this.quizData.petIndex === '') {
              // Call getPetIndex before navigating
              await this.getPetIndex(this.quizData.email);
            }
          }
          
          event.preventDefault();
          event.stopPropagation();
          
          // Handle pet type selection
          // First try explicit data-pet-type attribute
          const petTypeAttr = button.getAttribute('data-pet-type');
          const petTypeDataset = button.dataset.petType;
          let petType = petTypeAttr || petTypeDataset;
          
          // If not found, infer from data-next-slide (like feeding calculator does)
          if (!petType && nextSlideTarget) {
            const nextSlideLower = nextSlideTarget.toLowerCase().trim();
            // Direct match
            if (nextSlideLower === 'dog' || nextSlideLower === 'cat') {
              petType = nextSlideLower;
            } 
            // Contains dog/cat keywords
            else if (nextSlideLower.includes('dog')) {
              petType = 'dog';
            } else if (nextSlideLower.includes('cat')) {
              petType = 'cat';
            }
            // Check button text/label as fallback
            else {
              const buttonText = (button.textContent || '').toLowerCase().trim();
              if (buttonText.includes('dog') || buttonText === 'dog') {
                petType = 'dog';
              } else if (buttonText.includes('cat') || buttonText === 'cat') {
                petType = 'cat';
              }
            }
          }
          
          if (petType) {
            const normalizedPetType = petType.toLowerCase().trim();
            this.quizData.pet_type = normalizedPetType;
            this.quizData.petType = normalizedPetType; // Keep both for compatibility
            
            // Also store in a way that's immediately accessible
            this.currentPetType = normalizedPetType;
            this.updateProgressBar();
            
            // Collect all quiz data to ensure it's up to date
            this.collectQuizData();
            this.persistToLocalStorage();
            
            // Replace variables in current slide when pet type changes
            setTimeout(() => {
              this.replaceVariables();
            }, 50);
            
            // Update breed options immediately if pet-details slide exists
            const petDetailsSlide = this.slides.find(s => 
              s.classList.contains('pet-quiz-slide--pet-details') || 
              s.dataset.slideIdentifier === 'pet-details'
            );
            if (petDetailsSlide) {
              const breedSelect = petDetailsSlide.querySelector('[data-quiz-field="petBreed"]');
              if (breedSelect) {
                // Pass pet type directly to ensure it's used
                this.updateBreedOptions(breedSelect, normalizedPetType);
              }
            }
            
            // Also update any visible breed selectors
            const visibleBreedSelects = this.querySelectorAll('[data-quiz-field="petBreed"]:not([style*="display: none"])');
            visibleBreedSelects.forEach(select => {
              this.updateBreedOptions(select, normalizedPetType);
            });
            
            // Update brand search inputs when pet type changes
            const brandInputs = this.querySelectorAll('[data-quiz-field="petBrandFood"][type="text"]');
            brandInputs.forEach(input => {
              this.initBrandSearch(input, normalizedPetType);
            });
          }
          
          const targetSlide = this.findSlide(nextSlideTarget);
          if (targetSlide) {
            const slideIndex = parseInt(targetSlide.dataset.slideIndex, 10);
            this.navigateToSlide(slideIndex);
          }
          return;
        }
      }

      handleQuizSubmit(event) {
        const button = event.target.closest('[data-submit-quiz]');
        if (!button) return;
        
        const calculator = button.closest('pet-quiz');
        if (calculator !== this) return;
        
        event.preventDefault();
        event.stopPropagation();
        
        // Get redirect URL from button data attribute
        const redirectUrl = button.dataset.redirectUrl;
        if (!redirectUrl) {
          this.showSubmissionError('Redirect URL is not configured. Please contact support.');
          return;
        }
        
        // Show loading state on confirmation slide
        const confirmationSlide = this.slides[this.currentSlideIndex];
        if (confirmationSlide && confirmationSlide.classList.contains('pet-quiz-slide--confirmation')) {
          const loadingState = confirmationSlide.querySelector('[data-loading-state]');
          const errorMessage = confirmationSlide.querySelector('[data-error-message]');
          const submitButton = confirmationSlide.querySelector('[data-submit-quiz]');
          
          if (loadingState) loadingState.hidden = false;
          if (errorMessage) errorMessage.hidden = true;
          if (submitButton) submitButton.disabled = true;
        }
        
        // Submit to Gadget with redirect URL
        this.submitToGadget(redirectUrl);
      }

      resolvePetSpecificSlide(slideIdentifier) {
        // If the identifier is already pet-specific (contains -cat or -dog), return as-is
        if (slideIdentifier.includes('-cat') || slideIdentifier.includes('-dog')) {
          return slideIdentifier;
        }
        
        // Get current pet type
        const petType = this.currentPetType || this.quizData.pet_type || this.quizData.petType;
        if (!petType) {
          // If no pet type is set, return the identifier as-is
          return slideIdentifier;
        }
        
        const normalizedPetType = petType.toLowerCase().trim();
        
        // Resolve pet-specific identifiers
        if (slideIdentifier === 'pet-info' || slideIdentifier === 'pet-details') {
          return `${slideIdentifier}-${normalizedPetType}`;
        }
        
        // Return as-is if not a pet-specific identifier
        return slideIdentifier;
      }

      findSlide(target) {
        // Resolve pet-specific slide identifiers first
        const resolvedTarget = this.resolvePetSpecificSlide(target);
        
        // Try by data-slide-identifier
        let slide = this.slides.find(s => s.dataset.slideIdentifier === resolvedTarget);
        if (slide) return slide;
        
        // Also try the original target in case it's already pet-specific
        if (resolvedTarget !== target) {
          slide = this.slides.find(s => s.dataset.slideIdentifier === target);
          if (slide) return slide;
        }

        // Try by ID
        slide = this.querySelector(`#${resolvedTarget}`);
        if (slide && (slide.classList.contains('pet-quiz-slide') || slide.classList.contains('feeding-calculator-slide'))) {
          return slide;
        }
        
        // Try original target by ID
        if (resolvedTarget !== target) {
          slide = this.querySelector(`#${target}`);
          if (slide && (slide.classList.contains('pet-quiz-slide') || slide.classList.contains('feeding-calculator-slide'))) {
            return slide;
          }
        }

        // Try by slide number
        const slideNumberMatch = resolvedTarget.match(/slide-(\d+)/);
        if (slideNumberMatch) {
          const slideNumber = parseInt(slideNumberMatch[1], 10);
          slide = this.slides.find(s => parseInt(s.dataset.slideNumber, 10) === slideNumber);
          if (slide) return slide;
        }

        // Try by index
        const index = parseInt(resolvedTarget, 10);
        if (!isNaN(index) && index >= 0 && index < this.slides.length) {
          return this.slides[index];
        }

        return null;
      }

      findSlideById(slideId) {
        return this.slides.find(s => s.id === slideId) || 
               this.slides.find(s => `slide-${s.dataset.slideId}` === slideId);
      }

      navigateToSlide(index) {
        const currentSlide = this.slides[this.currentSlideIndex];
        if (currentSlide) {
          this.addToHistory(currentSlide, 'forward');
        }
        this.showSlide(index);
      }

      showSlide(index, skipTransition = false) {
        if (index < 0 || index >= this.slides.length) return;
        if (index === this.currentSlideIndex && !skipTransition) return;

        const previousSlide = this.slides[this.currentSlideIndex];
        const targetSlide = this.slides[index];

        if (!targetSlide) return;

        this.ensureDefaultSelections(targetSlide);

        if (this.isDesignMode || skipTransition) {
          const previousSlideHeight = previousSlide ? previousSlide.scrollHeight : null;
          
          this.slides.forEach(slide => {
            slide.classList.remove('pet-quiz-slide--active', 'feeding-calculator-slide--active');
          });
          
          targetSlide.classList.add('pet-quiz-slide--active', 'feeding-calculator-slide--active');
          this.currentSlideIndex = index;
          this.updateProgressBar();
          
          if (!skipTransition) {
            this.persistToLocalStorage();
          }
          
          setTimeout(() => {
            this.updateWrapperHeight(previousSlideHeight);
          }, 0);
          
          // Initialize slide-specific features
          this.initSlideFeatures(targetSlide);
          
          // Replace variables in slide content
          this.replaceVariables(targetSlide);
          
          // Collect quiz data to capture default values (e.g., checked checkboxes)
          this.collectQuizData();
          
          // Scroll to top when slide changes
          requestAnimationFrame(() => {
            // Try to scroll the quiz container to top first
            const quizContainer = this.closest('.pet-quiz-wrapper') || this;
            if (quizContainer && quizContainer.scrollIntoView) {
              quizContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            // Also scroll window to top as fallback
            window.scrollTo({ top: 0, behavior: 'smooth' });
          });
          
          // Dispatch slide change event
          this.dispatchEvent(new CustomEvent('slide:change', {
            detail: { slideIndex: index, slide: targetSlide }
          }));
          
          return;
        }

        // Replace variables BEFORE making slide visible to prevent flash of placeholders
        this.replaceVariables(targetSlide);
        
        // Initialize slide features before showing
        this.initSlideFeatures(targetSlide);
        
        // Crossfade transition
        targetSlide.classList.add('pet-quiz-slide--active', 'feeding-calculator-slide--active');
        targetSlide.style.opacity = '0';
        
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (previousSlide) {
              previousSlide.style.transition = 'opacity 0.3s ease';
              previousSlide.style.opacity = '0';
            }
            
            targetSlide.style.transition = 'opacity 0.3s ease';
            targetSlide.style.opacity = '1';
            
            setTimeout(() => {
              if (previousSlide) {
                previousSlide.classList.remove('pet-quiz-slide--active', 'feeding-calculator-slide--active');
                previousSlide.style.opacity = '';
                previousSlide.style.transition = '';
              }
              targetSlide.style.opacity = '';
              targetSlide.style.transition = '';
              
              this.currentSlideIndex = index;
              this.updateProgressBar();
              this.persistToLocalStorage();
              this.updateWrapperHeight();
              
              // Collect quiz data to capture default values (e.g., checked checkboxes)
              this.collectQuizData();
              
              // Scroll to top when slide changes
              requestAnimationFrame(() => {
                // Try to scroll the quiz container to top first
                const quizContainer = this.closest('.pet-quiz-wrapper') || this;
                if (quizContainer && quizContainer.scrollIntoView) {
                  quizContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
                // Also scroll window to top as fallback
                window.scrollTo({ top: 0, behavior: 'smooth' });
              });
              
              this.dispatchEvent(new CustomEvent('slide:change', {
                detail: { slideIndex: index, slide: targetSlide }
              }));
            }, 300);
          });
        });
      }

      initSlideFeatures(slide) {
        // Initialize breed search input
        const breedInput = slide.querySelector('[data-quiz-field="petBreed"][type="text"]');
        if (breedInput) {
          // Always ensure input is enabled
          breedInput.removeAttribute('disabled');
          breedInput.disabled = false;
          
          // Small delay to ensure quizData is up to date, but also try immediately
          const petType = this.currentPetType || this.quizData.pet_type || this.quizData.petType;
          if (petType) {
            this.initBreedSearch(breedInput, petType);
          } else {
            // Delay to allow pet type to be set
            setTimeout(() => {
              const delayedPetType = this.currentPetType || this.quizData.pet_type || this.quizData.petType;
              if (delayedPetType) {
                this.initBreedSearch(breedInput, delayedPetType);
              } else {
                // If still no pet type, initialize anyway (will show empty list)
                this.initBreedSearch(breedInput, null);
              }
            }, 100);
          }
        }
        
        // Also handle legacy select elements (for backwards compatibility)
        const breedSelect = slide.querySelector('select[data-quiz-field="petBreed"]');
        if (breedSelect && !breedInput) {
          const petType = this.currentPetType || this.quizData.pet_type || this.quizData.petType;
          if (petType) {
            this.updateBreedOptions(breedSelect, petType);
          } else {
            setTimeout(() => {
              const delayedPetType = this.currentPetType || this.quizData.pet_type || this.quizData.petType;
              this.updateBreedOptions(breedSelect, delayedPetType);
            }, 100);
          }
        }
        
        // Initialize option tag clouds (allergies, poo type, and change reason)
        const allergyContainer = slide.querySelector('[data-field-input="petAllergies"]');
        if (allergyContainer && !allergyContainer.dataset.initialized) {
          this.initOptionTagCloud(allergyContainer, 'petAllergies', [], true);
        }
        
        const changeReasonContainer = slide.querySelector('[data-field-input="petDietChangeReason"]');
        if (changeReasonContainer && !changeReasonContainer.dataset.initialized) {
          this.initOptionTagCloud(changeReasonContainer, 'petDietChangeReason', [], true);
        }
        
        // Initialize body type slider
        const bodyTypeSlider = slide.querySelector('[name="pet_body_type_slider"]');
        if (bodyTypeSlider && !bodyTypeSlider.dataset.initialized) {
          this.initBodyTypeSlider(bodyTypeSlider);
        }
        
        const activityLevelContainer = slide.querySelector('[data-field-input="petActivityLevel"]');
        if (activityLevelContainer && !activityLevelContainer.dataset.initialized) {
          this.initOptionTagCloud(activityLevelContainer, 'petActivityLevel', [], false);
        }
        
        const treatsContainer = slide.querySelector('[data-field-input="petEatsTreats"]');
        if (treatsContainer && !treatsContainer.dataset.initialized) {
          this.initOptionTagCloud(treatsContainer, 'petEatsTreats', [], false);
        }
        
        // Initialize food type buttons and slider
        const foodTypeButtons = slide.querySelector('[data-food-type-buttons]');
        if (foodTypeButtons && !foodTypeButtons.dataset.initialized) {
          this.initFoodTypeButtons(foodTypeButtons);
        }
        
        const foodTypeSlider = slide.querySelector('[name="pet_food_type_slider"]');
        if (foodTypeSlider && !foodTypeSlider.dataset.initialized) {
          this.initFoodTypeSlider(foodTypeSlider);
        }
        
        // Initialize poo type slider
        const pooTypeSlider = slide.querySelector('[name="pet_poo_type_slider"]');
        if (pooTypeSlider && !pooTypeSlider.dataset.initialized) {
          this.initPooTypeSlider(pooTypeSlider);
        }
        
        // Initialize brand search input
        const brandInput = slide.querySelector('[data-quiz-field="petBrandFood"][type="text"]');
        if (brandInput) {
          const petType = this.currentPetType || this.quizData.pet_type || this.quizData.petType;
          if (petType) {
            this.initBrandSearch(brandInput, petType);
          } else {
            setTimeout(() => {
              const delayedPetType = this.currentPetType || this.quizData.pet_type || this.quizData.petType;
              if (delayedPetType) {
                this.initBrandSearch(brandInput, delayedPetType);
              } else {
                this.initBrandSearch(brandInput, null);
              }
            }, 100);
          }
        }
        
        // Initialize confirmation summary
        const confirmationSummary = slide.querySelector('[data-confirmation-summary]');
        if (confirmationSummary) {
          this.updateConfirmationSummary(confirmationSummary);
        }
      }

      initBreedSelectors() {
        // Handle search inputs
        const breedInputs = this.querySelectorAll('[data-quiz-field="petBreed"][type="text"]');
        breedInputs.forEach((input) => {
          const petType = this.currentPetType || this.quizData.pet_type || this.quizData.petType;
          if (petType) {
            this.initBreedSearch(input, petType);
          }
        });
        
        // Handle legacy select elements
        const breedSelects = this.querySelectorAll('[data-quiz-field="petBreed"]:not([type="text"])');
        breedSelects.forEach((select) => {
          this.updateBreedOptions(select);
          
          // Watch for pet type changes
          const petTypeField = select.getAttribute('data-pet-type-field') || 'pet_type';
          select.addEventListener('change', () => {
            this.collectQuizData();
          });
        });
      }
      
      initBreedSearch(breedInput, providedPetType = null) {
        if (!breedInput) return;
        
        const slide = breedInput.closest('.pet-quiz-slide');
        if (!slide) return;
        
        const petTypeField = breedInput.getAttribute('data-pet-type-field') || 'pet_type';
        const petType = providedPetType || 
                       this.currentPetType ||
                       this.quizData[petTypeField] || 
                       this.quizData.pet_type || 
                       this.quizData.petType ||
                       this.quizData['pet-type'];
        
        // Always enable the input - don't disable it
        breedInput.disabled = false;
        
        if (!petType) {
          breedInput.placeholder = breedInput.placeholder || 'Select pet type first';
          // Don't return - still initialize with empty breeds so user can type
        }
        
        // Get breeds for pet type
        let breeds = [];
        
        if (petType) {
          const normalizedType = petType.toLowerCase().trim();
          
          // Try localStorage first
          if (typeof Storage !== 'undefined') {
            try {
              const cachedData = localStorage.getItem('petBreeds');
              if (cachedData) {
                const parsed = JSON.parse(cachedData);
                
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                  if (parsed[normalizedType] && Array.isArray(parsed[normalizedType])) {
                    breeds = parsed[normalizedType];
                  }
                } else if (Array.isArray(parsed)) {
                  breeds = parsed;
                }
              }
            } catch (e) {
              console.error('Pet Quiz: Error reading pet breeds from localStorage:', e);
            }
          }
          
          // Fallback to window.petBreeds
          if (breeds.length === 0 && window.petBreeds) {
            if (window.petBreeds[normalizedType] && Array.isArray(window.petBreeds[normalizedType])) {
              breeds = window.petBreeds[normalizedType];
            }
          }
          
          // Fallback to petBreedSelector
          if (breeds.length === 0 && window.petBreedSelector) {
            try {
              breeds = window.petBreedSelector.getPetBreeds(petType);
            } catch (e) {
              console.warn('Pet Quiz: Error getting breeds from petBreedSelector:', e);
            }
          }
        }
        
        // Add "Unknown" option
        breeds = ['Unknown', ...breeds];
        
        const blockId = slide.id.replace('slide-', '');
        const dropdown = slide.querySelector(`#pet-breed-dropdown-${blockId}`);
        const hiddenInput = slide.querySelector(`#pet-breed-value-${blockId}`);
        
        if (!dropdown || !hiddenInput) {
          console.warn('Pet Quiz: Breed dropdown or hidden input not found');
          return;
        }
        
        let filteredBreeds = breeds;
        let selectedIndex = -1;
        
        const renderDropdown = () => {
          dropdown.innerHTML = '';
          
          if (filteredBreeds.length === 0) {
            const noResults = document.createElement('div');
            noResults.className = 'pet-quiz-slide__breed-dropdown-item pet-quiz-slide__breed-dropdown-item--no-results';
            noResults.textContent = 'No breeds found';
            dropdown.appendChild(noResults);
            dropdown.style.display = 'block';
            return;
          }
          
          filteredBreeds.forEach((breed, index) => {
            const item = document.createElement('div');
            item.className = 'pet-quiz-slide__breed-dropdown-item';
            if (index === selectedIndex) {
              item.classList.add('pet-quiz-slide__breed-dropdown-item--selected');
            }
            item.textContent = typeof breed === 'string' ? breed : breed.name || breed.label || '';
            item.setAttribute('role', 'option');
            item.setAttribute('data-breed-value', typeof breed === 'string' ? breed : breed.value || breed.name || '');
            
            item.addEventListener('click', () => {
              const breedValue = item.getAttribute('data-breed-value');
              breedInput.value = item.textContent;
              hiddenInput.value = breedValue === 'Unknown' ? 'unknown' : breedValue;
              dropdown.style.display = 'none';
              
              // Sync "unsure of breed" checkbox if "Unknown" is selected
              const blockId = slide.id.replace('slide-', '');
              const unsureCheckbox = slide.querySelector(`#unsure-breed-${blockId}`);
              if (unsureCheckbox) {
                if (breedValue === 'Unknown' || breedValue === 'unknown') {
                  unsureCheckbox.checked = true;
                  breedInput.disabled = true;
                  breedInput.required = false;
                } else {
                  unsureCheckbox.checked = false;
                  breedInput.disabled = false;
                  breedInput.required = true;
                }
              }
              
              this.collectQuizData();
              this.persistToLocalStorage();
            });
            
            dropdown.appendChild(item);
          });
          
          dropdown.style.display = filteredBreeds.length > 0 ? 'block' : 'none';
        };
        
        // Track if dropdown should be shown
        let dropdownShouldShow = false;
        
        // Show dropdown only on click
        breedInput.addEventListener('click', () => {
          dropdownShouldShow = true;
          if (filteredBreeds.length > 0) {
            renderDropdown();
          }
        });
        
        // Filter breeds on input and show dropdown if it was opened
        breedInput.addEventListener('input', (e) => {
          const query = e.target.value.toLowerCase().trim();
          selectedIndex = -1;
          
          if (query === '') {
            filteredBreeds = breeds;
          } else {
            filteredBreeds = breeds.filter(breed => {
              const breedName = typeof breed === 'string' ? breed : breed.name || breed.label || '';
              return breedName.toLowerCase().includes(query);
            });
          }
          
          // Show dropdown when typing if it was previously opened
          if (dropdownShouldShow) {
            renderDropdown();
          }
        });
        
        // Handle keyboard navigation
        breedInput.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            dropdownShouldShow = true;
            selectedIndex = Math.min(selectedIndex + 1, filteredBreeds.length - 1);
            renderDropdown();
            const selectedItem = dropdown.querySelector(`.pet-quiz-slide__breed-dropdown-item:nth-child(${selectedIndex + 1})`);
            if (selectedItem) {
              selectedItem.scrollIntoView({ block: 'nearest' });
            }
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            dropdownShouldShow = true;
            selectedIndex = Math.max(selectedIndex - 1, -1);
            renderDropdown();
            if (selectedIndex >= 0) {
              const selectedItem = dropdown.querySelector(`.pet-quiz-slide__breed-dropdown-item:nth-child(${selectedIndex + 1})`);
              if (selectedItem) {
                selectedItem.scrollIntoView({ block: 'nearest' });
              }
            }
          } else if (e.key === 'Enter' && selectedIndex >= 0) {
            e.preventDefault();
            const selectedItem = dropdown.querySelector(`.pet-quiz-slide__breed-dropdown-item:nth-child(${selectedIndex + 1})`);
            if (selectedItem) {
              selectedItem.click();
            }
          } else if (e.key === 'Escape') {
            dropdown.style.display = 'none';
            dropdownShouldShow = false;
          }
        });
        
        // Close dropdown when clicking outside
        const handleClickOutside = (e) => {
          if (!breedInput.contains(e.target) && !dropdown.contains(e.target)) {
            dropdown.style.display = 'none';
            dropdownShouldShow = false;
          }
        };
        document.addEventListener('click', handleClickOutside);
        
        // Don't initialize dropdown automatically - wait for click
        dropdown.style.display = 'none';
        
        // Store cleanup function
        breedInput._breedSearchCleanup = () => {
          document.removeEventListener('click', handleClickOutside);
        };
        
        breedInput.dataset.initialized = 'true';
        breedInput.dataset.petType = petType || '';
        
        // Initialize "unsure of breed" checkbox handler
        this.initUnsureBreedCheckbox(breedInput);
      }
      
      initUnsureBreedCheckbox(breedInput) {
        const slide = breedInput.closest('.pet-quiz-slide');
        if (!slide) return;
        
        const blockId = slide.id.replace('slide-', '');
        const unsureCheckbox = slide.querySelector(`#unsure-breed-${blockId}`);
        if (!unsureCheckbox) return;
        
        // Check if breed is already set to "unknown"
        const hiddenInput = slide.querySelector(`#pet-breed-value-${blockId}`);
        if (hiddenInput && hiddenInput.value.toLowerCase() === 'unknown') {
          unsureCheckbox.checked = true;
          this.handleUnsureBreedCheckbox(unsureCheckbox);
        }
      }
      
      handleUnsureBreedCheckbox(checkbox) {
        const blockId = checkbox.getAttribute('data-breed-block-id');
        const slide = checkbox.closest('.pet-quiz-slide');
        if (!slide) return;
        
        const breedInput = slide.querySelector(`#pet-breed-${blockId}`);
        const hiddenInput = slide.querySelector(`#pet-breed-value-${blockId}`);
        const dropdown = slide.querySelector(`#pet-breed-dropdown-${blockId}`);
        
        if (!breedInput || !hiddenInput) return;
        
        if (checkbox.checked) {
          // Set breed to "Unknown"
          breedInput.value = 'Unknown';
          hiddenInput.value = 'unknown';
          breedInput.disabled = true;
          breedInput.required = false; // Remove required when unsure is checked
          if (dropdown) {
            dropdown.style.display = 'none';
          }
        } else {
          // Clear breed and enable input
          breedInput.value = '';
          hiddenInput.value = '';
          breedInput.disabled = false;
          breedInput.required = true; // Re-add required when unsure is unchecked
        }
        
        // Collect data and persist
        this.collectQuizData();
        this.persistToLocalStorage();
      }

      initBrandSearch(brandInput, providedPetType = null) {
        if (!brandInput) return;
        
        // Prevent re-initialization
        if (brandInput.dataset.initialized === 'true') {
          return;
        }
        
        const slide = brandInput.closest('.pet-quiz-slide');
        if (!slide) return;
        
        // Get brand lists from JSON script tag
        const blockId = slide.id.replace('slide-', '');
        const brandListsScript = slide.querySelector(`#brand-lists-${blockId}`);
        let brandLists = { dog: [], cat: [] };
        
        if (brandListsScript) {
          try {
            const parsed = JSON.parse(brandListsScript.textContent);
            // Simple parsing: one brand per line, just trim whitespace
            brandLists.dog = (parsed.dog || []).map(b => String(b).trim()).filter(b => b);
            brandLists.cat = (parsed.cat || []).map(b => String(b).trim()).filter(b => b);
          } catch (e) {
            console.error('Pet Quiz: Error parsing brand lists:', e);
          }
        }
        
        const petTypeField = brandInput.getAttribute('data-pet-type-field') || 'pet_type';
        const petType = providedPetType || 
                       this.currentPetType ||
                       this.quizData[petTypeField] || 
                       this.quizData.pet_type || 
                       this.quizData.petType ||
                       this.quizData['pet-type'];
        
        // Get brands for pet type
        let brands = [];
        if (petType) {
          const normalizedType = petType.toLowerCase().trim();
          if (normalizedType === 'dog' && brandLists.dog) {
            brands = brandLists.dog;
          } else if (normalizedType === 'cat' && brandLists.cat) {
            brands = brandLists.cat;
          } else {
            // If pet type doesn't match, combine both lists
            brands = [...brandLists.dog, ...brandLists.cat];
          }
        } else {
          // No pet type selected, show all brands
          brands = [...brandLists.dog, ...brandLists.cat];
        }
        
        // Remove duplicates and sort
        brands = [...new Set(brands)].sort();
        
        const dropdown = slide.querySelector(`#pet-brand-dropdown-${blockId}`);
        const hiddenInput = slide.querySelector(`#pet-brand-value-${blockId}`);
        
        if (!dropdown || !hiddenInput) {
          console.warn('Pet Quiz: Brand dropdown or hidden input not found');
          return;
        }
        
        let filteredBrands = brands;
        let selectedIndex = -1;
        
        const renderDropdown = () => {
          dropdown.innerHTML = '';
          
          if (filteredBrands.length === 0) {
            const noResults = document.createElement('div');
            noResults.className = 'pet-quiz-slide__breed-dropdown-item pet-quiz-slide__breed-dropdown-item--no-results';
            noResults.textContent = 'No brands found';
            dropdown.appendChild(noResults);
            dropdown.style.display = 'block';
            return;
          }
          
          filteredBrands.forEach((brand, index) => {
            const item = document.createElement('div');
            item.className = 'pet-quiz-slide__breed-dropdown-item';
            if (index === selectedIndex) {
              item.classList.add('pet-quiz-slide__breed-dropdown-item--selected');
            }
            item.textContent = brand.trim();
            item.setAttribute('role', 'option');
            item.setAttribute('data-brand-value', brand.trim());
            
            item.addEventListener('click', () => {
              const brandValue = item.getAttribute('data-brand-value');
              brandInput.value = item.textContent;
              hiddenInput.value = brandValue;
              dropdown.style.display = 'none';
              dropdownShouldShow = false;
              
              this.collectQuizData();
              this.persistToLocalStorage();
            });
            
            dropdown.appendChild(item);
          });
          
          // Always show dropdown when there are filtered brands
          dropdown.style.display = filteredBrands.length > 0 ? 'block' : 'none';
        };
        
        // Track if dropdown should be shown
        let dropdownShouldShow = false;
        
        // Show dropdown only on click
        brandInput.addEventListener('click', () => {
          dropdownShouldShow = true;
          if (filteredBrands.length > 0) {
            renderDropdown();
          }
        });
        
        // Filter brands on input and show dropdown
        brandInput.addEventListener('input', (e) => {
          const query = e.target.value.toLowerCase().trim();
          selectedIndex = -1;
          
          if (query === '') {
            filteredBrands = brands;
          } else {
            filteredBrands = brands.filter(brand => {
              return brand.toLowerCase().includes(query);
            });
          }
          
          // Show dropdown when typing
          dropdownShouldShow = true;
          renderDropdown();
          
          // Force show dropdown if there are results
          if (filteredBrands.length > 0) {
            dropdown.style.display = 'block';
          }
        });
        
        // Handle keyboard navigation
        brandInput.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            dropdownShouldShow = true;
            selectedIndex = Math.min(selectedIndex + 1, filteredBrands.length - 1);
            renderDropdown();
            const selectedItem = dropdown.querySelector(`.pet-quiz-slide__breed-dropdown-item:nth-child(${selectedIndex + 1})`);
            if (selectedItem) {
              selectedItem.scrollIntoView({ block: 'nearest' });
            }
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            dropdownShouldShow = true;
            selectedIndex = Math.max(selectedIndex - 1, -1);
            renderDropdown();
            if (selectedIndex >= 0) {
              const selectedItem = dropdown.querySelector(`.pet-quiz-slide__breed-dropdown-item:nth-child(${selectedIndex + 1})`);
              if (selectedItem) {
                selectedItem.scrollIntoView({ block: 'nearest' });
              }
            }
          } else if (e.key === 'Enter' && selectedIndex >= 0) {
            e.preventDefault();
            const selectedItem = dropdown.querySelector(`.pet-quiz-slide__breed-dropdown-item:nth-child(${selectedIndex + 1})`);
            if (selectedItem) {
              selectedItem.click();
            }
          } else if (e.key === 'Escape') {
            dropdown.style.display = 'none';
            dropdownShouldShow = false;
          }
        });
        
        // Close dropdown when clicking outside
        const handleClickOutside = (e) => {
          if (!brandInput.contains(e.target) && !dropdown.contains(e.target)) {
            dropdown.style.display = 'none';
            dropdownShouldShow = false;
          }
        };
        document.addEventListener('click', handleClickOutside);
        
        // Don't initialize dropdown automatically - wait for click
        dropdown.style.display = 'none';
        
        // Store cleanup function
        brandInput._brandSearchCleanup = () => {
          document.removeEventListener('click', handleClickOutside);
        };
        
        brandInput.dataset.initialized = 'true';
        brandInput.dataset.petType = petType || '';
      }

      updateBreedOptions(breedSelect, providedPetType = null) {
        if (!breedSelect) return;
        
        const petTypeField = breedSelect.getAttribute('data-pet-type-field') || 'pet_type';
        // Try multiple possible sources for pet type
        const petType = providedPetType || 
                       this.currentPetType ||
                       this.quizData[petTypeField] || 
                       this.quizData.pet_type || 
                       this.quizData.petType ||
                       this.quizData['pet-type'];
        
        if (!petType) {
          breedSelect.disabled = true;
          breedSelect.innerHTML = `
            <option value="" disabled selected>Select pet type first</option>
            <option value="unknown">Unknown</option>
          `;
          return;
        }
        
        breedSelect.disabled = false;
        
        // Get breeds for pet type
        const normalizedType = petType.toLowerCase().trim();
        let breeds = [];
        
        // Try localStorage first
        if (typeof Storage !== 'undefined') {
          try {
            const cachedData = localStorage.getItem('petBreeds');
            if (cachedData) {
              const parsed = JSON.parse(cachedData);
              
              if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                // Handle structure: {dog: [...], cat: [...]}
                if (parsed[normalizedType] && Array.isArray(parsed[normalizedType])) {
                  breeds = parsed[normalizedType];
                }
              } else if (Array.isArray(parsed)) {
                // Handle case where localStorage has array directly (shouldn't happen but handle it)
                breeds = parsed;
              }
            }
          } catch (e) {
            console.error('Pet Quiz: Error reading pet breeds from localStorage:', e);
          }
        }
        
        // Fallback to window.petBreeds
        if (breeds.length === 0 && window.petBreeds) {
          if (window.petBreeds[normalizedType] && Array.isArray(window.petBreeds[normalizedType])) {
            breeds = window.petBreeds[normalizedType];
          }
        }
        
        // Fallback to petBreedSelector
        if (breeds.length === 0 && window.petBreedSelector) {
          try {
            breeds = window.petBreedSelector.getPetBreeds(petType);
          } catch (e) {
            console.warn('Pet Quiz: Error getting breeds from petBreedSelector:', e);
          }
        }
        
        // Store current selection
        const currentValue = breedSelect.value;
        
        // Save placeholder and unknown option text before clearing
        const placeholderText = breedSelect.querySelector('option[value=""]')?.textContent || 'Search breed';
        const unknownText = breedSelect.querySelector('option[value="unknown"]')?.textContent || 'Unknown';
        
        // Clear existing options
        breedSelect.innerHTML = '';
        
        // Add placeholder
        const placeholderOption = document.createElement('option');
        placeholderOption.value = '';
        placeholderOption.textContent = placeholderText;
        placeholderOption.disabled = true;
        placeholderOption.selected = true;
        breedSelect.appendChild(placeholderOption);
        
        // Add unknown option
        const unknownOption = document.createElement('option');
        unknownOption.value = 'unknown';
        unknownOption.textContent = unknownText;
        breedSelect.appendChild(unknownOption);
        
        // Add breed options
        breeds.forEach((breed, index) => {
          const option = document.createElement('option');
          if (typeof breed === 'string') {
            option.value = breed;
            option.textContent = breed;
          } else if (breed && typeof breed === 'object') {
            // Handle {value: "...", label: "..."} structure
            option.value = breed.value || breed.label || '';
            option.textContent = breed.label || breed.value || '';
          } else {
            // Fallback for any other structure
            option.value = String(breed);
            option.textContent = String(breed);
          }
          breedSelect.appendChild(option);
        });
        
        // Restore selection if it still exists
        if (currentValue && breedSelect.querySelector(`option[value="${currentValue}"]`)) {
          breedSelect.value = currentValue;
        }
      }

      initOptionTagCloud(container, fieldName, options, allowMultiple = false) {
        if (container.dataset.initialized === 'true') return;
        
        // Fields that use .value from metaobjects
        const metaobjectValueFields = ['petBodyType', 'petActivityLevel', 'petPooType', 'petEatsTreats'];
        
        const slide = container.closest('.pet-quiz-slide');
        const blockId = slide ? slide.id.replace('slide-', '') : '';
        const showIcons = container.dataset.showIcons !== 'false'; // Default to true if not set
        
        // Get options from metaobject data (for allergies)
        if (fieldName === 'petAllergies' && blockId) {
          const allergensScript = slide.querySelector(`#allergens-data-${blockId}`);
          if (allergensScript) {
            try {
              const allergensData = JSON.parse(allergensScript.textContent);
              if (Array.isArray(allergensData) && allergensData.length > 0) {
                // Sort allergens: "none" first, then others by index
                allergensData.sort((a, b) => {
                  const nameA = typeof a === 'string' ? a : (a.name || '').toLowerCase().trim();
                  const nameB = typeof b === 'string' ? b : (b.name || '').toLowerCase().trim();
                  
                  // Put "none" first
                  if (nameA === 'none' && nameB !== 'none') return -1;
                  if (nameA !== 'none' && nameB === 'none') return 1;
                  
                  // For non-"none" items, sort by index
                  const indexA = a.index !== undefined ? a.index : 9999;
                  const indexB = b.index !== undefined ? b.index : 9999;
                  return indexA - indexB;
                });
                
                options = allergensData;
              }
            } catch (e) {
              console.error('Pet Quiz: Failed to parse allergens data', e);
              return; // Don't initialize if metaobject data is invalid
            }
          } else {
            console.warn('Pet Quiz: Allergens metaobject data not found');
            return; // Don't initialize if metaobject data is missing
          }
        }
        
        // Get options from metaobject data (for poo type)
        if (fieldName === 'petPooType' && blockId) {
          const pooTypesScript = slide.querySelector(`#poo-types-data-${blockId}`);
          if (pooTypesScript) {
            try {
              const pooTypesData = JSON.parse(pooTypesScript.textContent);
              if (Array.isArray(pooTypesData) && pooTypesData.length > 0) {
                // Sort by index if available
                pooTypesData.sort((a, b) => {
                  const indexA = a.index !== undefined ? a.index : 9999;
                  const indexB = b.index !== undefined ? b.index : 9999;
                  return indexA - indexB;
                });
                options = pooTypesData;
              }
            } catch (e) {
              console.error('Pet Quiz: Failed to parse poo types data', e);
              return; // Don't initialize if metaobject data is invalid
            }
          } else {
            console.warn('Pet Quiz: Poo types metaobject data not found');
            return; // Don't initialize if metaobject data is missing
          }
        }
        
        // Get options from metaobject data (for body type)
        if (fieldName === 'petBodyType' && blockId) {
          const bodyTypesScript = slide.querySelector(`#body-types-data-${blockId}`);
          if (bodyTypesScript) {
            try {
              const bodyTypesData = JSON.parse(bodyTypesScript.textContent);
              if (Array.isArray(bodyTypesData) && bodyTypesData.length > 0) {
                // Sort by index if available
                bodyTypesData.sort((a, b) => {
                  const indexA = a.index !== undefined ? a.index : 9999;
                  const indexB = b.index !== undefined ? b.index : 9999;
                  return indexA - indexB;
                });
                options = bodyTypesData;
              }
            } catch (e) {
              console.error('Pet Quiz: Failed to parse body types data', e);
              return; // Don't initialize if metaobject data is invalid
            }
          } else {
            console.warn('Pet Quiz: Body types metaobject data not found');
            return; // Don't initialize if metaobject data is missing
          }
        }
        
        // Get options from metaobject data (for activity level)
        if (fieldName === 'petActivityLevel' && blockId) {
          // Try ID selector first, then fallback to data-block-id attribute
          let activityLevelsScript = slide.querySelector(`#activity-levels-data-${blockId}`);
          if (!activityLevelsScript) {
            // Fallback: find script by data-block-id attribute
            activityLevelsScript = slide.querySelector(`script[data-block-id="${blockId}"]`);
          }
          if (activityLevelsScript) {
            try {
              const activityLevelsData = JSON.parse(activityLevelsScript.textContent);
              if (Array.isArray(activityLevelsData) && activityLevelsData.length > 0) {
                // Sort by index if available
                activityLevelsData.sort((a, b) => {
                  const indexA = a.index !== undefined ? a.index : 9999;
                  const indexB = b.index !== undefined ? b.index : 9999;
                  return indexA - indexB;
                });
                options = activityLevelsData;
              }
            } catch (e) {
              console.error('Pet Quiz: Failed to parse activity levels data', e);
              return; // Don't initialize if metaobject data is invalid
            }
          } else {
            console.warn('Pet Quiz: Activity levels metaobject data not found for blockId:', blockId);
            return; // Don't initialize if metaobject data is missing
          }
        }
        
        // Get options from metaobject data (for treats)
        if (fieldName === 'petEatsTreats' && blockId) {
          const treatsScript = slide.querySelector(`#treats-options-data-${blockId}`);
          if (treatsScript) {
            try {
              const treatsData = JSON.parse(treatsScript.textContent);
              if (Array.isArray(treatsData) && treatsData.length > 0) {
                // Sort by index if available
                treatsData.sort((a, b) => {
                  const indexA = a.index !== undefined ? a.index : 9999;
                  const indexB = b.index !== undefined ? b.index : 9999;
                  return indexA - indexB;
                });
                options = treatsData;
              }
            } catch (e) {
              console.error('Pet Quiz: Failed to parse treats options data', e);
              return; // Don't initialize if metaobject data is invalid
            }
          } else {
            console.warn('Pet Quiz: Treats options metaobject data not found for blockId:', blockId);
            return; // Don't initialize if metaobject data is missing
          }
        }
        
        // Get options from metaobject data (for change reason)
        if (fieldName === 'petDietChangeReason' && blockId) {
          const changeReasonsScript = slide.querySelector(`#change-reasons-data-${blockId}`);
          if (changeReasonsScript) {
            try {
              const changeReasonsData = JSON.parse(changeReasonsScript.textContent);
              if (Array.isArray(changeReasonsData) && changeReasonsData.length > 0) {
                // Randomize the order of change reasons
                const shuffledReasons = [...changeReasonsData];
                for (let i = shuffledReasons.length - 1; i > 0; i--) {
                  const j = Math.floor(Math.random() * (i + 1));
                  [shuffledReasons[i], shuffledReasons[j]] = [shuffledReasons[j], shuffledReasons[i]];
                }
                // Add "Other" option at the end
                options = [...shuffledReasons, { name: 'Other', value: 'other' }];
              } else {
                // Even if no metaobject data, add "Other" option
                options = [{ name: 'Other', value: 'other' }];
              }
            } catch (e) {
              console.error('Pet Quiz: Failed to parse change reasons data', e);
              // Still add "Other" option even if parsing fails
              options = [{ name: 'Other', value: 'other' }];
            }
          } else {
            console.warn('Pet Quiz: Change reasons metaobject data not found');
            // Still add "Other" option even if data is missing
            options = [{ name: 'Other', value: 'other' }];
          }
        }
        
        const hiddenInput = container.querySelector(`[data-quiz-field="${fieldName}"]`) || container.querySelector(`[data-${fieldName.toLowerCase().replace(/([A-Z])/g, '-$1').toLowerCase()}-input]`);
        let selectedValues = [];
        
        if (hiddenInput && hiddenInput.value) {
          if (allowMultiple) {
            try {
              selectedValues = JSON.parse(hiddenInput.value);
              if (!Array.isArray(selectedValues)) selectedValues = [];
            } catch (e) {
              selectedValues = [];
            }
          } else {
            selectedValues = hiddenInput.value ? [hiddenInput.value] : [];
          }
        }
        
        // Normalize selected values for fields that use .value from metaobjects
        const normalizedSelected = selectedValues.map(v => {
          if (metaobjectValueFields.includes(fieldName)) {
            const matchingOption = options.find(opt => {
              if (typeof opt === 'object' && opt.value) {
                return opt.value.toLowerCase().trim() === String(v).toLowerCase().trim() ||
                       opt.name.toLowerCase().trim() === String(v).toLowerCase().trim();
              }
              return String(opt).toLowerCase().trim() === String(v).toLowerCase().trim();
            });
            return matchingOption && typeof matchingOption === 'object' && matchingOption.value 
              ? matchingOption.value.toLowerCase().trim()
              : String(v).toLowerCase().trim();
          }
          return String(v).toLowerCase().trim();
        });
        
        const tagCloudHTML = options.map(option => {
          // Fields that use .value from metaobjects
          const optionValue = typeof option === 'string' ? option : (option.value || option.name || option.label || '');
          const optionLabel = typeof option === 'string' ? option : (option.label || option.name || option.value || '');
          const optionImage = showIcons && typeof option === 'object' && option.image ? option.image : '';
          const useValueField = metaobjectValueFields.includes(fieldName) && typeof option === 'object' && option.value;
          const optionValueForComparison = useValueField
            ? option.value.toLowerCase().trim() 
            : optionValue.toLowerCase().trim();
          const isSelected = normalizedSelected.includes(optionValueForComparison);
          const isActivityOrBodyType = metaobjectValueFields.includes(fieldName);
          const labelTag = isActivityOrBodyType ? 'h5' : 'span';
          // Store additional_info for body type if available
          // For richtext HTML content, we only escape quotes for the HTML attribute, preserving HTML tags
          const additionalInfo = (fieldName === 'petBodyType' && typeof option === 'object' && option.additional_info) ? option.additional_info : '';
          // Only escape quotes for HTML attribute safety, preserve HTML tags and existing entities
          const additionalInfoEscaped = additionalInfo ? additionalInfo.replace(/"/g, '&quot;') : '';
          // Store subtitle for activity level if available
          const subtitle = (fieldName === 'petActivityLevel' && typeof option === 'object' && option.subtitle) ? option.subtitle : '';
          const hasSubtitle = subtitle && subtitle.trim() !== '';
          return `
            <button 
              type="button"
              class="pet-quiz-slide__option-tag ${isSelected ? 'pet-quiz-slide__option-tag--selected' : ''}"
              data-option-value="${this.escapeHtml(optionValueForComparison)}"
              data-option-name="${this.escapeHtml(optionLabel)}"
              ${useValueField ? `data-option-value-field="${this.escapeHtml(option.value)}"` : ''}
              ${additionalInfo ? `data-additional-info="${additionalInfoEscaped}"` : ''}
              aria-pressed="${isSelected}"
            >
              ${optionImage ? `<img src="${this.escapeHtml(optionImage)}" alt="${this.escapeHtml(optionLabel)}" height="50" width="50" class="pet-quiz-slide__option-tag-icon" loading="lazy">` : ''}
              <div class="pet-quiz-slide__option-tag-content">
                <${labelTag} class="pet-quiz-slide__option-tag-label">${this.escapeHtml(optionLabel)}</${labelTag}>
                ${hasSubtitle ? `<span class="pet-quiz-slide__option-tag-subtitle">${this.escapeHtml(subtitle)}</span>` : ''}
              </div>
            </button>
          `;
        }).join('');
        
        if (!hiddenInput) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.setAttribute('data-quiz-field', fieldName);
          input.value = allowMultiple ? JSON.stringify(selectedValues) : (selectedValues[0] || '');
          container.appendChild(input);
        } else if (allowMultiple && !hiddenInput.value) {
          hiddenInput.value = JSON.stringify(selectedValues);
        }
        
        container.insertAdjacentHTML('beforeend', tagCloudHTML);
        container.dataset.initialized = 'true';
        container.dataset.allowMultiple = allowMultiple;
        
        // Show additional_info for pre-selected body type on initialization
        if (fieldName === 'petBodyType' && !allowMultiple && selectedValues.length > 0) {
          const selectedValue = selectedValues[0];
          const selectedTagButton = container.querySelector(`[data-option-value="${selectedValue.toLowerCase().trim()}"]`);
          if (selectedTagButton) {
            const blockId = slide.id.replace('slide-', '');
            const additionalInfoContainer = slide.querySelector(`#body-type-additional-info-${blockId}`);
            const additionalInfoAttr = selectedTagButton.getAttribute('data-additional-info');
            
            if (additionalInfoContainer && additionalInfoAttr && additionalInfoAttr.trim() !== '') {
              // Decode only &quot; back to quotes, preserve HTML tags and entities
              const additionalInfoHtml = additionalInfoAttr.replace(/&quot;/g, '"');
              additionalInfoContainer.innerHTML = additionalInfoHtml;
              additionalInfoContainer.style.display = 'block';
            }
          }
        }
        
        // Set up event listeners for tag buttons
        container.querySelectorAll('.pet-quiz-slide__option-tag').forEach(tagButton => {
          tagButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleOptionTag(tagButton);
          });
        });
        
        // Set up event listener for "Other" input field (change reason)
        if (fieldName === 'petDietChangeReason') {
          const otherWrapper = slide.querySelector('.pet-quiz-slide__other-reason-wrapper');
          const otherInput = slide.querySelector('[data-quiz-field="petDietChangeReasonOther"]');
          
          // Show "Other" input if "other" is already selected
          if (normalizedSelected.includes('other')) {
            if (otherWrapper) otherWrapper.style.display = 'block';
          }
          
          if (otherInput) {
            otherInput.addEventListener('input', () => {
              this.collectQuizData();
              this.persistToLocalStorage();
              
              // Clear validation error if user is typing
              if (otherInput.value && otherInput.value.trim()) {
                otherInput.classList.remove('error');
                otherInput.removeAttribute('aria-invalid');
                const errorId = `error-${fieldName}-other`;
                const errorDiv = slide.querySelector(`[data-validation-error="${errorId}"]`);
                if (errorDiv) {
                  errorDiv.remove();
                }
              }
            });
            otherInput.addEventListener('change', () => {
              this.collectQuizData();
              this.persistToLocalStorage();
              
              // Clear validation error if user has entered a value
              if (otherInput.value && otherInput.value.trim()) {
                otherInput.classList.remove('error');
                otherInput.removeAttribute('aria-invalid');
                const errorId = `error-${fieldName}-other`;
                const errorDiv = slide.querySelector(`[data-validation-error="${errorId}"]`);
                if (errorDiv) {
                  errorDiv.remove();
                }
              }
            });
          }
        }
      }

      toggleOptionTag(tagButton) {
        const tagCloud = tagButton.closest('[data-field-input]');
        if (!tagCloud) return;
        
        const fieldName = tagCloud.getAttribute('data-field-input');
        const hiddenInput = tagCloud.querySelector(`[data-quiz-field="${fieldName}"]`);
        if (!hiddenInput) return;
        
        const slide = tagCloud.closest('.pet-quiz-slide');
        const allowMultiple = tagCloud.dataset.allowMultiple === 'true';
        const optionValue = tagButton.getAttribute('data-option-value');
        const normalizedValue = optionValue.toLowerCase().trim();
        
        // Handle "Other" option for change reason
        if (fieldName === 'petDietChangeReason' && normalizedValue === 'other') {
          const otherWrapper = slide ? slide.querySelector('.pet-quiz-slide__other-reason-wrapper') : null;
          const otherInput = slide ? slide.querySelector('[data-quiz-field="petDietChangeReasonOther"]') : null;
          
          if (tagButton.classList.contains('pet-quiz-slide__option-tag--selected')) {
            // Deselecting "Other" - hide input and clear value
            if (otherWrapper) otherWrapper.style.display = 'none';
            if (otherInput) otherInput.value = '';
          } else {
            // Selecting "Other" - show input
            if (otherWrapper) otherWrapper.style.display = 'block';
            if (otherInput) otherInput.focus();
          }
        }
        
        if (allowMultiple) {
          let selectedValues = [];
          try {
            selectedValues = JSON.parse(hiddenInput.value || '[]');
            if (!Array.isArray(selectedValues)) selectedValues = [];
          } catch (e) {
            selectedValues = [];
          }
          
          const index = selectedValues.findIndex(v => String(v).toLowerCase().trim() === normalizedValue);
          
          if (index >= 0) {
            selectedValues.splice(index, 1);
            tagButton.classList.remove('pet-quiz-slide__option-tag--selected');
            tagButton.setAttribute('aria-pressed', 'false');
            
            // If deselecting "Other", hide the input
            if (fieldName === 'petDietChangeReason' && normalizedValue === 'other') {
              const otherWrapper = slide ? slide.querySelector('.pet-quiz-slide__other-reason-wrapper') : null;
              const otherInput = slide ? slide.querySelector('[data-quiz-field="petDietChangeReasonOther"]') : null;
              if (otherWrapper) otherWrapper.style.display = 'none';
              if (otherInput) otherInput.value = '';
            }
          } else {
            // For allergies field, "none" is mutually exclusive with other options
            if (fieldName === 'petAllergies' && normalizedValue === 'none') {
              // Selecting "none" - clear all other selections
              selectedValues = ['none'];
              tagCloud.querySelectorAll('.pet-quiz-slide__option-tag').forEach(tag => {
                tag.classList.remove('pet-quiz-slide__option-tag--selected');
                tag.setAttribute('aria-pressed', 'false');
              });
              tagButton.classList.add('pet-quiz-slide__option-tag--selected');
              tagButton.setAttribute('aria-pressed', 'true');
            } else if (fieldName === 'petAllergies' && normalizedValue !== 'none') {
              // Selecting something other than "none" - remove "none" if it exists
              const noneIndex = selectedValues.findIndex(v => String(v).toLowerCase().trim() === 'none');
              if (noneIndex >= 0) {
                selectedValues.splice(noneIndex, 1);
                // Also visually deselect the "none" button
                tagCloud.querySelectorAll('.pet-quiz-slide__option-tag').forEach(tag => {
                  const tagValue = tag.getAttribute('data-option-value');
                  if (tagValue && tagValue.toLowerCase().trim() === 'none') {
                    tag.classList.remove('pet-quiz-slide__option-tag--selected');
                    tag.setAttribute('aria-pressed', 'false');
                  }
                });
              }
              selectedValues.push(normalizedValue);
              tagButton.classList.add('pet-quiz-slide__option-tag--selected');
              tagButton.setAttribute('aria-pressed', 'true');
            } else {
              // For other fields, allow normal multiple selection
              selectedValues.push(normalizedValue);
              tagButton.classList.add('pet-quiz-slide__option-tag--selected');
              tagButton.setAttribute('aria-pressed', 'true');
            }
          }
          hiddenInput.value = JSON.stringify(selectedValues);
          
          // Clear validation errors when an option is selected (for required fields)
          if (hiddenInput.hasAttribute('data-required') || hiddenInput.hasAttribute('required')) {
            const tagCloud = hiddenInput.closest('[data-field-input]');
            if (tagCloud && selectedValues.length > 0 && selectedValues[0] !== '') {
              tagCloud.classList.remove('error');
              const errorId = `error-${fieldName}`;
              const errorDiv = slide?.querySelector(`[data-validation-error="${errorId}"]`);
              if (errorDiv) {
                errorDiv.remove();
              }
              
              // For change reason, also check if "other" is selected and filled
              if (fieldName === 'petDietChangeReason') {
                const hasOther = selectedValues.some(v => String(v).toLowerCase().trim() === 'other');
                if (!hasOther) {
                  // If "other" is not selected, clear any "other" input errors
                  const otherInput = slide?.querySelector('[data-quiz-field="petDietChangeReasonOther"]');
                  if (otherInput) {
                    otherInput.classList.remove('error');
                    otherInput.removeAttribute('aria-invalid');
                    const otherErrorId = `error-${fieldName}-other`;
                    const otherErrorDiv = slide?.querySelector(`[data-validation-error="${otherErrorId}"]`);
                    if (otherErrorDiv) {
                      otherErrorDiv.remove();
                    }
                  }
                }
              }
            }
          }
        } else {
          // For single-selection fields that use .value from metaobjects
          const valueField = tagButton.getAttribute('data-option-value-field');
          const valueToCompare = valueField ? valueField.toLowerCase().trim() : normalizedValue;
          const valueToStore = valueField || tagButton.getAttribute('data-option-name') || normalizedValue;
          
          const currentValue = hiddenInput.value.toLowerCase().trim();
          if (currentValue === valueToCompare) {
            hiddenInput.value = '';
            tagButton.classList.remove('pet-quiz-slide__option-tag--selected');
            tagButton.setAttribute('aria-pressed', 'false');
            
            // Hide additional_info when body type is deselected
            if (fieldName === 'petBodyType' && slide) {
              const slideBlockId = slide.id.replace('slide-', '');
              const additionalInfoContainer = slide.querySelector(`#body-type-additional-info-${slideBlockId}`);
              if (additionalInfoContainer) {
                additionalInfoContainer.style.display = 'none';
                additionalInfoContainer.innerHTML = '';
              }
            }
          } else {
            tagCloud.querySelectorAll('.pet-quiz-slide__option-tag').forEach(tag => {
              tag.classList.remove('pet-quiz-slide__option-tag--selected');
              tag.setAttribute('aria-pressed', 'false');
          });
            hiddenInput.value = valueToStore;
            tagButton.classList.add('pet-quiz-slide__option-tag--selected');
            tagButton.setAttribute('aria-pressed', 'true');
            
            // Show additional_info when body type is selected
            if (fieldName === 'petBodyType' && slide) {
              const blockId = slide.id.replace('slide-', '');
              const additionalInfoContainer = slide.querySelector(`#body-type-additional-info-${blockId}`);
              const additionalInfoAttr = tagButton.getAttribute('data-additional-info');
              
              if (additionalInfoContainer) {
                if (additionalInfoAttr && additionalInfoAttr.trim() !== '') {
                  // Decode only &quot; back to quotes (browser should handle this automatically, but being explicit)
                  // HTML tags and other entities are preserved as-is
                  const additionalInfoHtml = additionalInfoAttr.replace(/&quot;/g, '"');
                  additionalInfoContainer.innerHTML = additionalInfoHtml;
                  additionalInfoContainer.style.display = 'block';
                } else {
                  additionalInfoContainer.style.display = 'none';
                  additionalInfoContainer.innerHTML = '';
                }
              }
            }
          }
        }
        
        this.collectQuizData();
        this.persistToLocalStorage();
      }

      // Helper: Extract .value from metaobject option, with fallback to name
      getMetaobjectValue(option, fieldName = '') {
        const value = option.value;
        if (!value || value === '') {
          if (this.debugMode && fieldName) {
            console.warn(`Pet Quiz: ${fieldName} option missing value field:`, option);
          }
          return option.name || '';
        }
        return value;
      }

      initFoodTypeButtons(buttonsContainer) {
        if (buttonsContainer.dataset.initialized === 'true') return;
        
        const slide = buttonsContainer.closest('.pet-quiz-slide');
        if (!slide) return;
        
        const sliderSection = slide.querySelector('[data-food-type-slider-section]');
        const slider = slide.querySelector('[name="pet_food_type_slider"]');
        const hiddenInput = slide.querySelector('[name="pet_food_type"]');
        const buttons = buttonsContainer.querySelectorAll('[data-food-type-button]');
        
        const updateButtonStates = (selectedType) => {
          buttons.forEach(button => {
            const buttonType = button.getAttribute('data-food-type');
            if (buttonType === selectedType) {
              button.classList.add('pet-quiz-slide__option-tag--selected');
              button.setAttribute('aria-pressed', 'true');
            } else {
              button.classList.remove('pet-quiz-slide__option-tag--selected');
              button.setAttribute('aria-pressed', 'false');
            }
          });
        };
        
        const handleFoodTypeSelection = (foodType, isToggle = false) => {
          // If toggling and this type is already selected, deselect it
          if (isToggle && foodType) {
            const currentType = hiddenInput?.value?.toLowerCase() || this.quizData.petFoodType?.toLowerCase();
            if (currentType === foodType.toLowerCase()) {
              // Deselect - clear the selection
              if (hiddenInput) {
                hiddenInput.value = '';
              }
              
              // Clear button states
              buttons.forEach(button => {
                button.classList.remove('pet-quiz-slide__option-tag--selected');
                button.setAttribute('aria-pressed', 'false');
              });
              
              // Hide slider section
              if (sliderSection) {
                sliderSection.style.display = 'none';
              }
              
              // Clear quiz data
              delete this.quizData.petFoodType;
              delete this.quizData.wetFoodRatio;
              delete this.quizData.dryFoodRatio;
              
              this.collectQuizData();
              this.persistToLocalStorage();
              return;
            }
          }
          
          // If no foodType provided, don't do anything
          if (!foodType) return;
          
          // Update hidden input
          if (hiddenInput) {
            hiddenInput.value = foodType;
          }
          
          // Update slider value behind the scenes
          if (slider) {
            if (foodType === 'wet') {
              slider.value = 0;
            } else if (foodType === 'dry') {
              slider.value = 100;
            } else {
              // Mix - default to 50/50
              slider.value = 50;
            }
            
            // Trigger slider update if initialized
            if (slider.dataset.initialized === 'true') {
              const event = new Event('input', { bubbles: true });
              slider.dispatchEvent(event);
            }
          }
          
          // Show/hide slider section
          if (sliderSection) {
            if (foodType === 'mix') {
              sliderSection.style.display = '';
            } else {
              sliderSection.style.display = 'none';
            }
          }
          
          // Update button states
          updateButtonStates(foodType);
          
          // Store in quizData
          this.quizData.petFoodType = foodType;
          if (foodType === 'wet') {
            this.quizData.wetFoodRatio = 100;
            this.quizData.dryFoodRatio = 0;
            this.quizData.petFoodMixRatio = '100/0';
          } else if (foodType === 'dry') {
            this.quizData.wetFoodRatio = 0;
            this.quizData.dryFoodRatio = 100;
            this.quizData.petFoodMixRatio = '0/100';
          } else if (foodType === 'mix') {
            // For mix, ratios are set by slider, but ensure petFoodMixRatio is set
            const sliderValue = slider ? parseInt(slider.value, 10) : 50;
            this.quizData.wetFoodRatio = sliderValue;
            this.quizData.dryFoodRatio = 100 - sliderValue;
            this.quizData.petFoodMixRatio = `${sliderValue}/${100 - sliderValue}`;
          }
          
          this.collectQuizData();
          this.persistToLocalStorage();
        };
        
        // Initialize from existing value (but don't auto-select)
        let initialType = null;
        
        // Check if there's an existing saved value (don't use slider default value of 50)
        if (hiddenInput && hiddenInput.value && hiddenInput.value.trim() !== '') {
          initialType = hiddenInput.value.toLowerCase();
        } else if (this.quizData.petFoodType) {
          initialType = this.quizData.petFoodType.toLowerCase();
        } else if (slider && slider.dataset.initialized === 'true') {
          // Only use slider value if it's explicitly set (not the default 50)
          const sliderValue = parseInt(slider.value, 10);
          if (sliderValue === 0) {
            initialType = 'wet';
          } else if (sliderValue === 100) {
            initialType = 'dry';
          }
          // Don't set mix for default slider value of 50
        }
        
        // Only initialize if there's a valid existing value (not default)
        if (initialType && ['wet', 'dry', 'mix'].includes(initialType)) {
          handleFoodTypeSelection(initialType, false);
        }
        
        // Add click handlers to buttons with toggle functionality
        buttons.forEach(button => {
          button.addEventListener('click', () => {
            const foodType = button.getAttribute('data-food-type');
            handleFoodTypeSelection(foodType, true);
          });
        });
        
        buttonsContainer.dataset.initialized = 'true';
      }

      initFoodTypeSlider(slider) {
        if (slider.dataset.initialized === 'true') return;
        
        const slide = slider.closest('.pet-quiz-slide');
        if (!slide) return;
        
        const blockId = slide.id.replace('slide-', '');
        const hiddenInput = slide.querySelector('[name="pet_food_type"]');
        const wetPercentageEl = slide.querySelector(`#wet-percentage-${blockId}`);
        const dryPercentageEl = slide.querySelector(`#dry-percentage-${blockId}`);
        const ratioEl = slide.querySelector(`#food-type-ratio-${blockId}`);
        const sliderSection = slide.querySelector('[data-food-type-slider-section]');
        
        // Map slider value to food type: 0 = wet, 50 = mix, 100 = dry
        const updateFoodType = (value, updateInput = true, updateQuizData = true) => {
          let foodType = 'mix';
          let wetPercent = 50;
          let dryPercent = 50;
          let ratioText = 'Mix';
          
          if (value === 0) {
            foodType = 'wet';
            wetPercent = 100;
            dryPercent = 0;
            ratioText = 'Wet';
          } else if (value === 100) {
            foodType = 'dry';
            wetPercent = 0;
            dryPercent = 100;
            ratioText = 'Dry';
          } else {
            foodType = 'mix';
            wetPercent = value;
            dryPercent = 100 - value;
            ratioText = `${wetPercent}/${dryPercent}`;
          }
          
          if (hiddenInput && updateInput) {
            hiddenInput.value = foodType;
          }
          
          if (wetPercentageEl) {
            wetPercentageEl.textContent = `${wetPercent}%`;
          }
          
          if (dryPercentageEl) {
            dryPercentageEl.textContent = `${dryPercent}%`;
          }
          
          if (ratioEl) {
            ratioEl.textContent = ratioText;
          }
          
          // Only store ratios in quizData if updateQuizData is true (user interaction)
          if (updateQuizData) {
            this.quizData.petFoodType = foodType;
            this.quizData.wetFoodRatio = wetPercent;
            this.quizData.dryFoodRatio = dryPercent;
            this.quizData.petFoodMixRatio = `${wetPercent}/${dryPercent}`;
          }
          
          // Update button states if buttons exist
          const buttonsContainer = slide.querySelector('[data-food-type-buttons]');
          if (buttonsContainer && buttonsContainer.dataset.initialized === 'true') {
            const buttons = buttonsContainer.querySelectorAll('[data-food-type-button]');
            buttons.forEach(button => {
              const buttonType = button.getAttribute('data-food-type');
              if (buttonType === foodType) {
                button.classList.add('pet-quiz-slide__option-tag--selected');
                button.setAttribute('aria-pressed', 'true');
              } else {
                button.classList.remove('pet-quiz-slide__option-tag--selected');
                button.setAttribute('aria-pressed', 'false');
              }
            });
            
            // Show/hide slider section based on food type
            if (sliderSection) {
              if (foodType === 'mix') {
                sliderSection.style.display = '';
              } else {
                sliderSection.style.display = 'none';
              }
            }
          }
          
          this.collectQuizData();
          this.persistToLocalStorage();
        };
        
        // Initialize from existing value if present (only if user has explicitly selected)
        if (hiddenInput && hiddenInput.value && hiddenInput.value.trim() !== '') {
          const currentType = hiddenInput.value.toLowerCase();
          if (currentType === 'wet') {
            slider.value = 0;
            updateFoodType(0, true, true); // updateInput=true, updateQuizData=true (restoring saved value)
          } else if (currentType === 'dry') {
            slider.value = 100;
            updateFoodType(100, true, true);
          } else if (currentType === 'mix') {
            slider.value = 50;
            updateFoodType(50, true, true);
          }
        } else if (this.quizData.petFoodType && this.quizData.petFoodType.trim() !== '') {
          // Use quizData if available (only if explicitly set, not default)
          const currentType = this.quizData.petFoodType.toLowerCase();
          if (currentType === 'wet') {
            slider.value = 0;
            updateFoodType(0, true, true);
          } else if (currentType === 'dry') {
            slider.value = 100;
            updateFoodType(100, true, true);
          } else if (currentType === 'mix') {
            slider.value = 50;
            updateFoodType(50, true, true);
          }
        }
        // Don't call updateFoodType if there's no existing value - let user select
        
        // Listen for slider changes (user interaction - always update quizData)
        slider.addEventListener('input', (event) => {
          const value = parseInt(event.target.value, 10);
          updateFoodType(value, true, true); // User interaction - update quizData
        });
        
        slider.addEventListener('change', (event) => {
          const value = parseInt(event.target.value, 10);
          updateFoodType(value, true, true); // User interaction - update quizData
        });
        
        slider.dataset.initialized = 'true';
      }

      initBodyTypeSlider(slider) {
        if (slider.dataset.initialized === 'true') return;
        
        const slide = slider.closest('.pet-quiz-slide');
        if (!slide) return;
        
        const blockId = slide.id.replace('slide-', '');
        const hiddenInput = slide.querySelector('[name="pet_body_type"]');
        const imagesContainer = slide.querySelector(`#body-type-images-container-${blockId}`);
        const additionalInfoContainer = slide.querySelector(`#body-type-additional-info-${blockId}`);
        
        // Get body types from metaobject data
        const bodyTypesScript = slide.querySelector(`#body-types-data-${blockId}`);
        if (!bodyTypesScript) {
          console.warn('Pet Quiz: Body types metaobject data not found');
          // Still initialize slider with default values to prevent blocking
          slider.dataset.initialized = 'true';
          return;
        }
        
        let bodyTypes = [];
        try {
          bodyTypes = JSON.parse(bodyTypesScript.textContent);
          if (!Array.isArray(bodyTypes) || bodyTypes.length === 0) {
            console.warn('Pet Quiz: No body types found in metaobject data');
            // Still initialize slider to prevent blocking
            slider.dataset.initialized = 'true';
            return;
          }
        } catch (e) {
          console.error('Pet Quiz: Failed to parse body types data', e);
          // Still initialize slider to prevent blocking
          slider.dataset.initialized = 'true';
          return;
        }
        
        // Sort body types by index
        bodyTypes.sort((a, b) => {
          const indexA = a.index !== undefined ? a.index : 9999;
          const indexB = b.index !== undefined ? b.index : 9999;
          return indexA - indexB;
        });
        
        // Get min and max index values from sorted options
        // First option (lowest index) = min, last option (highest index) = max
        const minIndex = bodyTypes.length > 0 ? (bodyTypes[0].index !== undefined ? bodyTypes[0].index : 1) : 1;
        const maxIndex = bodyTypes.length > 0 ? (bodyTypes[bodyTypes.length - 1].index !== undefined ? bodyTypes[bodyTypes.length - 1].index : bodyTypes.length) : 1;
        
        slider.min = minIndex;
        slider.max = maxIndex;
        slider.step = 1;
        
        // Set initial value to first option's index
        if (!hiddenInput || !hiddenInput.value) {
          slider.value = minIndex;
        }
        
        // Create all body type images above the slider in flexbox
        if (imagesContainer && bodyTypes.length > 0) {
          imagesContainer.innerHTML = '';
          const initialSliderValue = parseInt(slider.value || minIndex, 10);
          
          bodyTypes.forEach((bodyType, idx) => {
            const bodyTypeIndex = bodyType.index !== undefined ? bodyType.index : idx + 1;
            const imageWrapper = document.createElement('div');
            imageWrapper.className = 'pet-quiz-slide__body-type-image-wrapper';
            imageWrapper.dataset.bodyTypeIndex = bodyTypeIndex;
            
            if (bodyType.image) {
              const img = document.createElement('img');
              img.src = bodyType.image;
              img.alt = bodyType.name || '';
              img.className = 'pet-quiz-slide__body-type-image';
              img.width = 50;
              img.height = 50;
              img.loading = 'lazy';
              // Set initial opacity based on whether this is the selected option
              img.style.opacity = bodyTypeIndex === initialSliderValue ? '1' : '0.5';
              // Make image cursor pointer to indicate it's clickable
              img.style.cursor = 'pointer';
              imageWrapper.appendChild(img);
            }
            
            // Add click handler to image wrapper to update slider
            imageWrapper.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              
              // Set slider value to this body type's index
              slider.value = bodyTypeIndex;
              
              // Trigger updateBodyType to update UI and form data
              updateBodyType(bodyTypeIndex);
            });
            
            // Make wrapper clickable too
            imageWrapper.style.cursor = 'pointer';
            
            imagesContainer.appendChild(imageWrapper);
          });
        }
        
        // Map slider value (which is the index) to body type option
        const updateBodyType = (value) => {
          const sliderIndex = parseInt(value, 10);
          
          // Find option with matching index
          const selectedOption = bodyTypes.find(option => {
            const optionIndex = option.index !== undefined ? option.index : 9999;
            return optionIndex === sliderIndex;
          });
          
          if (selectedOption) {
            const optionName = selectedOption.name || '';
            const optionValue = this.getMetaobjectValue(selectedOption, 'petBodyType');
            
            if (hiddenInput) hiddenInput.value = optionValue;
            
            // Update image opacity: selected = 1, others = 0.5
            if (imagesContainer) {
              const imageWrappers = imagesContainer.querySelectorAll('.pet-quiz-slide__body-type-image-wrapper');
              imageWrappers.forEach(wrapper => {
                const wrapperIndex = parseInt(wrapper.dataset.bodyTypeIndex, 10);
                const img = wrapper.querySelector('img');
                if (img) {
                  if (wrapperIndex === sliderIndex) {
                    img.style.opacity = '1';
                  } else {
                    img.style.opacity = '0.5';
                  }
                }
              });
            }
            
            // Update additional_info container with body type name and additional info
            if (additionalInfoContainer) {
              let content = `<strong>${this.escapeHtml(optionName)}</strong>`;
              const info = selectedOption.additional_info || '';
              if (info.trim()) {
                content += '<br>' + info.replace(/&quot;/g, '"');
              }
              additionalInfoContainer.innerHTML = content;
              additionalInfoContainer.style.display = 'block';
            }
            
            // Images are positioned via CSS flexbox, no repositioning needed
            
            this.quizData.petBodyType = optionValue;
            this.collectQuizData();
            this.persistToLocalStorage();
          }
        };
        
        // Initialize from existing value if present
        if (hiddenInput && hiddenInput.value) {
          const currentValue = hiddenInput.value.trim();
          const matchingOption = bodyTypes.find(opt => {
            const optVal = opt.value || '';
            const optName = opt.name || '';
            return optVal.toLowerCase().trim() === currentValue.toLowerCase().trim() ||
                   optName.toLowerCase().trim() === currentValue.toLowerCase().trim();
          });
          if (matchingOption) {
            slider.value = matchingOption.index || bodyTypes.indexOf(matchingOption) + 1;
            const correctValue = this.getMetaobjectValue(matchingOption, 'petBodyType');
            hiddenInput.value = correctValue;
          }
        }
        
        // Always call updateBodyType to ensure hidden input has .value from metaobject
        updateBodyType(slider.value);
        
        // Double-check: verify hidden input has .value, not name
        if (hiddenInput && hiddenInput.value) {
          const currentHiddenValue = hiddenInput.value.trim();
          const verifyOption = bodyTypes.find(opt => {
            const optName = opt.name || '';
            return optName.toLowerCase().trim() === currentHiddenValue.toLowerCase().trim();
          });
          // If hidden input has a name instead of value, fix it
          if (verifyOption && verifyOption.value && verifyOption.value !== currentHiddenValue) {
            hiddenInput.value = verifyOption.value;
            if (this.debugMode) {
              console.log('Pet Quiz: Fixed petBodyType hidden input', {
                was: currentHiddenValue,
                now: verifyOption.value
              });
            }
          }
        }
        
        // Listen for slider changes
        slider.addEventListener('input', (event) => {
          const value = parseInt(event.target.value, 10);
          updateBodyType(value);
        });
        
        slider.addEventListener('change', (event) => {
          const value = parseInt(event.target.value, 10);
          updateBodyType(value);
        });
        
        slider.dataset.initialized = 'true';
      }

      initPooTypeSlider(slider) {
        if (slider.dataset.initialized === 'true') return;
        
        const slide = slider.closest('.pet-quiz-slide');
        if (!slide) return;
        
        const blockId = slide.id.replace('slide-', '');
        const hiddenInput = slide.querySelector('[name="pet_poo_type"]');
        const imagesContainer = slide.querySelector(`#poo-type-images-container-${blockId}`);
        const pooTypeNameDisplay = slide.querySelector(`#poo-type-name-display-${blockId}`);
        const infoBoxEl = slide.querySelector('.pet-quiz-slide__info-box');
        
        // Get poo types from metaobject data
        const pooTypesScript = slide.querySelector(`#poo-types-data-${blockId}`);
        if (!pooTypesScript) {
          console.warn('Pet Quiz: Poo types metaobject data not found');
          // Still initialize slider to prevent blocking
          slider.dataset.initialized = 'true';
          return;
        }
        
        let pooTypes = [];
        try {
          pooTypes = JSON.parse(pooTypesScript.textContent);
          if (!Array.isArray(pooTypes) || pooTypes.length === 0) {
            console.warn('Pet Quiz: No poo types found in metaobject data');
            // Still initialize slider to prevent blocking
            slider.dataset.initialized = 'true';
            return;
          }
          
          // Debug: Log first option to verify .value field exists
          if (this.debugMode && pooTypes.length > 0) {
            console.log('Pet Quiz: Poo types metaobject data sample:', {
              firstOption: pooTypes[0],
              hasValue: !!pooTypes[0].value,
              value: pooTypes[0].value,
              name: pooTypes[0].name
            });
          }
        } catch (e) {
          console.error('Pet Quiz: Failed to parse poo types data', e);
          // Still initialize slider to prevent blocking
          slider.dataset.initialized = 'true';
          return;
        }
        
        // Sort poo types by index
        pooTypes.sort((a, b) => {
          const indexA = a.index !== undefined ? a.index : 9999;
          const indexB = b.index !== undefined ? b.index : 9999;
          return indexA - indexB;
        });
        
        // Get min and max index values from sorted options
        // First option (lowest index) = min, last option (highest index) = max
        const minIndex = pooTypes.length > 0 ? (pooTypes[0].index !== undefined ? pooTypes[0].index : 1) : 1;
        const maxIndex = pooTypes.length > 0 ? (pooTypes[pooTypes.length - 1].index !== undefined ? pooTypes[pooTypes.length - 1].index : pooTypes.length) : 1;
        
        slider.min = minIndex;
        slider.max = maxIndex;
        slider.step = 1;
        
        // Set initial value to first option's index
        if (!hiddenInput || !hiddenInput.value) {
          slider.value = minIndex;
        }
        
        // Create all poo type images above the slider in flexbox
        if (imagesContainer && pooTypes.length > 0) {
          imagesContainer.innerHTML = '';
          const initialSliderValue = parseInt(slider.value || minIndex, 10);
          
          pooTypes.forEach((pooType, idx) => {
            const pooTypeIndex = pooType.index !== undefined ? pooType.index : idx + 1;
            const imageWrapper = document.createElement('div');
            imageWrapper.className = 'pet-quiz-slide__poo-type-image-wrapper';
            imageWrapper.dataset.pooTypeIndex = pooTypeIndex;
            
            if (pooType.image) {
              const img = document.createElement('img');
              img.src = pooType.image;
              img.alt = pooType.name || '';
              img.className = 'pet-quiz-slide__poo-type-image';
              img.width = 50;
              img.height = 50;
              img.loading = 'lazy';
              // Set initial opacity based on whether this is the selected option
              img.style.opacity = pooTypeIndex === initialSliderValue ? '1' : '0.5';
              // Make image cursor pointer to indicate it's clickable
              img.style.cursor = 'pointer';
              imageWrapper.appendChild(img);
            }
            
            // Add click handler to image wrapper to update slider
            imageWrapper.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              
              // Set slider value to this poo type's index
              slider.value = pooTypeIndex;
              
              // Trigger updatePooType to update UI and form data
              updatePooType(pooTypeIndex);
            });
            
            // Make wrapper clickable too
            imageWrapper.style.cursor = 'pointer';
            
            imagesContainer.appendChild(imageWrapper);
          });
        }
        
        // Map slider value (which is the index) to poo type option
        const updatePooType = (value) => {
          const sliderIndex = parseInt(value, 10);
          
          // Find option with matching index
          const selectedOption = pooTypes.find(option => {
            const optionIndex = option.index !== undefined ? option.index : 9999;
            return optionIndex === sliderIndex;
          });
          
          if (selectedOption) {
            const optionName = selectedOption.name || '';
            const optionValue = this.getMetaobjectValue(selectedOption, 'petPooType');
            
            // Debug logging
            if (this.debugMode) {
              console.log('Pet Quiz: updatePooType', {
                selectedOption,
                optionName,
                optionValue,
                hasValueField: !!selectedOption.value,
                rawValue: selectedOption.value
              });
            }
            
            if (hiddenInput) {
              hiddenInput.value = optionValue;
              // Verify it was set correctly
              if (this.debugMode && hiddenInput.value !== optionValue) {
                console.error('Pet Quiz: Failed to set hidden input value!', {
                  expected: optionValue,
                  actual: hiddenInput.value
                });
              }
            }
            
            // Update image opacity: selected = 1, others = 0.5
            if (imagesContainer) {
              const imageWrappers = imagesContainer.querySelectorAll('.pet-quiz-slide__poo-type-image-wrapper');
              imageWrappers.forEach(wrapper => {
                const wrapperIndex = parseInt(wrapper.dataset.pooTypeIndex, 10);
                const img = wrapper.querySelector('img');
                if (img) {
                  if (wrapperIndex === sliderIndex) {
                    img.style.opacity = '1';
                  } else {
                    img.style.opacity = '0.5';
                  }
                }
              });
            }
            
            // Update poo type name display under slider
            if (pooTypeNameDisplay) {
              pooTypeNameDisplay.textContent = optionName;
            }
            
            // Hide info box if "perfect" poo type is selected
            if (infoBoxEl) {
              const isPerfect = optionName.toLowerCase().includes('perfect');
              infoBoxEl.style.display = isPerfect ? 'none' : '';
            }
            
            this.quizData.petPooType = optionValue;
            this.collectQuizData();
            this.persistToLocalStorage();
          }
        };
        
        // Initialize from existing value if present
        if (hiddenInput && hiddenInput.value) {
          const currentValue = hiddenInput.value.trim();
          const matchingOption = pooTypes.find(opt => {
            const optVal = opt.value || '';
            const optName = opt.name || '';
            return optVal.toLowerCase().trim() === currentValue.toLowerCase().trim() ||
                   optName.toLowerCase().trim() === currentValue.toLowerCase().trim();
          });
          if (matchingOption) {
            slider.value = matchingOption.index || pooTypes.indexOf(matchingOption) + 1;
            const correctValue = this.getMetaobjectValue(matchingOption, 'petPooType');
            hiddenInput.value = correctValue;
            if (this.debugMode) {
              console.log('Pet Quiz: Restored petPooType', {
                currentValue,
                correctValue,
                matchingOption
              });
            }
          } else if (this.debugMode) {
            console.warn('Pet Quiz: Could not find matching poo type for:', currentValue);
          }
        }
        
        // Always call updatePooType to ensure hidden input has .value from metaobject
        // This handles both initial load and restoration cases
        updatePooType(slider.value);
        
        // Double-check: verify hidden input has .value, not name
        if (hiddenInput && hiddenInput.value) {
          const currentHiddenValue = hiddenInput.value.trim();
          const verifyOption = pooTypes.find(opt => {
            const optName = opt.name || '';
            return optName.toLowerCase().trim() === currentHiddenValue.toLowerCase().trim();
          });
          // If hidden input has a name instead of value, fix it
          if (verifyOption && verifyOption.value && verifyOption.value !== currentHiddenValue) {
            hiddenInput.value = verifyOption.value;
            if (this.debugMode) {
              console.log('Pet Quiz: Fixed petPooType hidden input', {
                was: currentHiddenValue,
                now: verifyOption.value
              });
            }
          }
        }
        
        // Listen for slider changes
        slider.addEventListener('input', (event) => {
          const value = parseInt(event.target.value, 10);
          updatePooType(value);
        });
        
        slider.addEventListener('change', (event) => {
          const value = parseInt(event.target.value, 10);
          updatePooType(value);
        });
        
        slider.dataset.initialized = 'true';
      }

      replaceVariables(slide = null) {
        const targetSlide = slide || this.slides[this.currentSlideIndex];
        if (!targetSlide) return;
        
        // Replace [[pet_name]] and other variables in slide content
        const petName = this.quizData.petName || this.quizData.pet_name || '';
        const petTypeRaw = this.quizData.petType || this.quizData.pet_type || '';
        // Format petType: capitalize first letter (Dog/Cat)
        const petType = petTypeRaw ? petTypeRaw.charAt(0).toUpperCase() + petTypeRaw.slice(1).toLowerCase() : '';
        
        if (!petName && !petType) return; // No point processing if no data
        
        // Pattern to match various pet name placeholder formats
        const petNamePatterns = [
          /\[\[pet_name\]\]/gi,
          /\[\[pet's name\]\]/gi,
          /\[\[pet's_name\]\]/gi,
          /\[\[petName\]\]/gi,
          /\[\[pet-name\]\]/gi
        ];
        
        // Pattern to match various pet type placeholder formats
        const petTypePatterns = [
          /\[\[pet_type\]\]/gi,
          /\[\[petType\]\]/gi,
          /\[\[pet-type\]\]/gi
        ];
        
        // Replace in all text nodes (handles plain text content)
        const walker = document.createTreeWalker(
          targetSlide,
          NodeFilter.SHOW_TEXT,
          null,
          false
        );
        
        const textNodes = [];
        let node;
        while (node = walker.nextNode()) {
          if (node.textContent.includes('[[')) {
            textNodes.push(node);
          }
        }
        
        textNodes.forEach(textNode => {
          let text = textNode.textContent;
          // Replace all pet name variations
          petNamePatterns.forEach(pattern => {
            text = text.replace(pattern, petName);
          });
          // Replace all pet type variations
          petTypePatterns.forEach(pattern => {
            text = text.replace(pattern, petType);
          });
          textNode.textContent = text;
        });
        
        // Replace in HTML attributes (title, aria-label, placeholder, data-*, etc.)
        // Include the slide element itself and all descendants
        const allElements = [targetSlide, ...targetSlide.querySelectorAll('*')];
        allElements.forEach(el => {
          // Process all attributes
          Array.from(el.attributes).forEach(attr => {
            if (attr.value && attr.value.includes('[[')) {
              let newValue = attr.value;
              // Replace all pet name variations
              petNamePatterns.forEach(pattern => {
                newValue = newValue.replace(pattern, petName);
              });
              // Replace all pet type variations
              petTypePatterns.forEach(pattern => {
                newValue = newValue.replace(pattern, petType);
              });
              if (newValue !== attr.value) {
                el.setAttribute(attr.name, newValue);
              }
            }
          });
        });
        
        // Replace in HTML attributes and content for data-pet-name-placeholder elements
        targetSlide.querySelectorAll('[data-pet-name-placeholder]').forEach(el => {
          if (petName) {
            el.textContent = petName;
          }
        });
      }

      updateConfirmationSummary(container) {
        if (!container) return;
        
        const slide = container.closest('.pet-quiz-slide--confirmation');
        if (!slide) return;
        
        // Helper function to format activity level
        const formatActivityLevel = (activityLevel) => {
          if (!activityLevel) return null;
          if (typeof activityLevel === 'string') {
            const trimmed = activityLevel.trim();
            if (trimmed === '[]' || trimmed.startsWith('[') || trimmed === '') {
              return null;
            }
            return trimmed
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
          }
          return activityLevel;
        };
        
        // Helper function to format date
        const formatDate = (dateString) => {
          if (!dateString) return null;
          try {
            const date = new Date(dateString);
            if (!isNaN(date.getTime())) {
              return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit' 
              });
            }
          } catch (e) {
            return dateString;
          }
          return dateString;
        };
        
        // Helper function to format allergies
        const formatAllergies = (allergies) => {
          if (!allergies || !Array.isArray(allergies) || allergies.length === 0) {
            return 'None';
          }
          const validAllergies = allergies.filter(a => a && a.toLowerCase() !== 'none');
          return validAllergies.length > 0 ? validAllergies.join(', ') : 'None';
        };
        
        // Build table rows with only required fields
        const rows = [];
        
        // Pet Type
        const petType = this.quizData.petType || '';
        rows.push({
          label: 'Pet Type',
          value: petType ? petType.charAt(0).toUpperCase() + petType.slice(1) : 'Not set',
          field: 'petType'
        });
        
        // Weight
        const weight = this.quizData.petWeight;
        rows.push({
          label: 'Weight',
          value: weight ? `${weight} kg` : 'Not set',
          field: 'petWeight'
        });
        
        // Activity Level
        const activityLevel = formatActivityLevel(this.quizData.petActivityLevel);
        rows.push({
          label: 'Activity Level',
          value: activityLevel || 'Not set',
          field: 'petActivityLevel'
        });
        
        // Birthday (support both petBirthday and petDateOfBirth for API/dataset compatibility)
        const birthdateValue = this.quizData.petBirthday || this.quizData.petDateOfBirth;
        const birthday = formatDate(birthdateValue);
        rows.push({
          label: 'Birthday',
          value: birthday || 'Not set',
          field: 'petBirthday'
        });
        
        // Food Type
        const foodType = this.quizData.petFoodType || '';
        let foodTypeDisplay = foodType ? foodType.charAt(0).toUpperCase() + foodType.slice(1) : 'Not set';
        if (foodType === 'mix' && this.quizData.dryFoodRatio !== undefined && this.quizData.wetFoodRatio !== undefined) {
          foodTypeDisplay += ` (${Math.round(this.quizData.dryFoodRatio)}/${Math.round(this.quizData.wetFoodRatio)})`;
        }
        rows.push({
          label: 'Food Type',
          value: foodTypeDisplay,
          field: 'petFoodType'
        });
        
        // Allergies
        const allergies = formatAllergies(this.quizData.petAllergies);
        rows.push({
          label: 'Allergies',
          value: allergies,
          field: 'petAllergies'
        });
        
        // Build table HTML
        const tableHTML = rows.map(row => `
          <tr class="pet-quiz-slide__confirmation-row">
            <th class="pet-quiz-slide__confirmation-label">${this.escapeHtml(row.label)}</th>
            <td class="pet-quiz-slide__confirmation-value">${this.escapeHtml(row.value)}</td>
            <td class="pet-quiz-slide__confirmation-edit-cell">
              <button type="button" class="pet-quiz-slide__confirmation-edit" data-edit-field="${this.escapeHtml(row.field)}">Edit</button>
            </td>
          </tr>
        `).join('');
        
        container.innerHTML = tableHTML;
        
        // Show correct image based on pet type
        const imageWrapper = slide.querySelector('.pet-quiz-slide__confirmation-image-wrapper');
        if (imageWrapper) {
          const dogImage = imageWrapper.querySelector('[data-pet-type="dog"]');
          const catImage = imageWrapper.querySelector('[data-pet-type="cat"]');
          
          if (petType.toLowerCase() === 'dog' && dogImage) {
            dogImage.hidden = false;
            if (catImage) catImage.hidden = true;
          } else if (petType.toLowerCase() === 'cat' && catImage) {
            catImage.hidden = false;
            if (dogImage) dogImage.hidden = true;
          } else {
            if (dogImage) dogImage.hidden = true;
            if (catImage) catImage.hidden = true;
          }
        }
        
        // Add edit handlers - open drawer instead of navigating
        container.querySelectorAll('[data-edit-field]').forEach(button => {
          button.addEventListener('click', (e) => {
            e.preventDefault();
            this.openConfirmationDrawer();
          });
        });
      }
      
      async openConfirmationDrawer() {
        // Ensure drawer component is loaded
        if (!customElements.get('pet-profile-drawer')) {
          // Load the drawer script if not already loaded
          if (!document.querySelector('script[src*="pet-profile-drawer.js"]')) {
            const script = document.createElement('script');
            // Get asset URL from existing script tags or use relative path
            const existingScript = document.querySelector('script[src*="pet-quiz.js"]');
            if (existingScript) {
              const basePath = existingScript.src.substring(0, existingScript.src.lastIndexOf('/'));
              script.src = `${basePath}/pet-profile-drawer.js`;
            } else {
              script.src = '/assets/pet-profile-drawer.js';
            }
            script.defer = true;
            document.head.appendChild(script);
            await customElements.whenDefined('pet-profile-drawer');
          } else {
            await customElements.whenDefined('pet-profile-drawer');
          }
        }
        
        // Get or create drawer
        let drawer = document.getElementById('PetProfileDrawer');
        
        if (!drawer) {
          // Check if there's a template in the page
          const template = document.getElementById('pet-profile-drawer-template');
          if (template) {
            drawer = template.content.firstElementChild || template.content.querySelector('pet-profile-drawer');
            if (drawer) {
              document.body.appendChild(drawer);
            }
          } else {
            console.error('Pet Quiz: Pet profile drawer template not found');
            return;
          }
        }
        
        // Convert quiz data to pet profile format
        // Normalize petEatsTreats from boolean to string if needed
        let petEatsTreats = this.quizData.petEatsTreats || '';
        if (typeof petEatsTreats === 'boolean') {
          petEatsTreats = petEatsTreats ? 'yes' : 'no';
        } else if (petEatsTreats === '') {
          petEatsTreats = '';
        }
        
        const petData = {
          id: 'quiz-temp', // Temporary ID for quiz data
          petName: this.quizData.petName || '',
          petType: this.quizData.petType || '',
          petGender: this.quizData.petGender || '',
          petBreed: this.quizData.petBreed || '',
          petWeight: this.quizData.petWeight || null,
          petActivityLevel: this.quizData.petActivityLevel || '',
          petBodyType: this.quizData.petBodyType || '',
          petBirthday: this.quizData.petBirthday || this.quizData.petDateOfBirth || null,
          petFoodType: this.quizData.petFoodType || '',
          dryFoodRatio: this.quizData.dryFoodRatio || null,
          wetFoodRatio: this.quizData.wetFoodRatio || null,
          petBrandFood: this.quizData.petBrandFood || '',
          petAllergies: Array.isArray(this.quizData.petAllergies) ? this.quizData.petAllergies : [],
          petDietChangeReason: Array.isArray(this.quizData.petDietChangeReason) ? this.quizData.petDietChangeReason : (this.quizData.petDietChangeReason ? [this.quizData.petDietChangeReason] : []),
          petPooType: this.quizData.petPooType || '',
          isPetRescue: this.quizData.isPetRescue || false,
          petEatsTreats: petEatsTreats
        };
        
        // Get brand lists from quiz section
        let brandLists = { dog: [], cat: [] };
        const quizSection = this.closest('.section');
        if (quizSection) {
          // Try to find brand lists from food preference slide
          const foodPreferenceSlide = this.querySelector('.pet-quiz-slide--food-preference, [data-slide-identifier="food-preference"]');
          if (foodPreferenceSlide) {
            const blockId = foodPreferenceSlide.id.replace('slide-', '');
            const brandListsScript = foodPreferenceSlide.querySelector(`#brand-lists-${blockId}`);
            if (brandListsScript) {
              try {
                const parsed = JSON.parse(brandListsScript.textContent);
                brandLists.dog = (parsed.dog || []).map(b => String(b).trim()).filter(b => b);
                brandLists.cat = (parsed.cat || []).map(b => String(b).trim()).filter(b => b);
              } catch (e) {
                console.error('Pet Quiz: Error parsing brand lists:', e);
              }
            }
          }
        }
        
        // Show drawer
        if (drawer.show) {
          // Store reference to quiz component for quiz mode
          drawer.quizComponent = this;
          drawer.dataset.quizMode = 'true';
          // Store brand lists for quiz mode
          drawer.brandLists = brandLists;
          
          // Store original save method if not already stored
          if (!drawer._originalSaveAllChanges) {
            drawer._originalSaveAllChanges = drawer.saveAllChanges.bind(drawer);
          }
          
          // Override saveAllChanges to update quiz data instead of saving to backend
          drawer.saveAllChanges = async function() {
            // Check if in quiz mode
            if (this.dataset.quizMode === 'true' && this.quizComponent) {
              // Prevent multiple simultaneous saves
              if (this.saving) {
                return;
              }
              
              if (Object.keys(this.changes).length === 0) {
                return;
              }
              
              // Validate: if petFoodType is "mix", ensure ratios are present
              const petFoodType = this.changes.petFoodType !== undefined 
                ? this.changes.petFoodType 
                : this.currentPet.petFoodType;
              const foodType = String(petFoodType || '').toLowerCase().trim();
              
              if (foodType === 'mix') {
                const hasDryRatio = (this.changes.dryFoodRatio !== undefined) || 
                                   (this.currentPet.dryFoodRatio !== null && this.currentPet.dryFoodRatio !== undefined);
                const hasWetRatio = (this.changes.wetFoodRatio !== undefined) || 
                                   (this.currentPet.wetFoodRatio !== null && this.currentPet.wetFoodRatio !== undefined);
                
                if (!hasDryRatio || !hasWetRatio) {
                  alert('Please set both dry and wet food ratios before saving.');
                  return;
                }
              }
              
              this.saving = true;
              const saveBtn = this.querySelector('[data-save-all]');
              if (saveBtn) {
                saveBtn.disabled = true;
                saveBtn.classList.add('loading');
              }
              
              try {
                // Finish editing any active field first to ensure changes are captured
                if (this.editingFieldKey) {
                  this.finishEditingField(this.editingFieldKey, true);
                }
                
                // Get changes from drawer
                const changes = this.changes || {};
                let currentPet = this.currentPet || {};
                
                // Apply changes to currentPet first (merge changes with currentPet)
                const updatedPet = { ...currentPet };
                Object.keys(changes).forEach(key => {
                  if (changes[key] !== undefined) {
                    updatedPet[key] = changes[key];
                  }
                });
                
                // Update drawer's currentPet with merged data
                this.currentPet = updatedPet;
                
                // Now sync all fields to quiz data (including unchanged fields)
                Object.keys(updatedPet).forEach(key => {
                  if (key !== 'id' && key !== 'petProfileImage' && key !== 'petProfileImagePreview') {
                    // Only sync if value is not null/undefined, or if it's explicitly set to null (for clearing)
                    if (updatedPet[key] !== undefined) {
                      this.quizComponent.quizData[key] = updatedPet[key];
                    }
                  }
                });
                
                // Handle special cases
                // If petFoodType changed to non-mix, clear ratios
                if (updatedPet.petFoodType && updatedPet.petFoodType.toLowerCase() !== 'mix') {
                  delete this.quizComponent.quizData.dryFoodRatio;
                  delete this.quizComponent.quizData.wetFoodRatio;
                }
                
                // Debug logging
                if (this.quizComponent.debugMode) {
                  console.log('Pet Quiz: Updated quiz data from drawer:', {
                    changes: changes,
                    updatedPet: updatedPet,
                    quizData: this.quizComponent.quizData
                  });
                }
                
                // Clear changes and update original data
                this.changes = {};
                this.originalPetData = JSON.parse(JSON.stringify(this.currentPet));
                this.updateFooterVisibility();
                this.updateSaveButton();
                
                // Update form fields to match updated quiz data
                // This ensures form fields reflect the changes made in the drawer
                this.quizComponent.restoreFormData();
                
                // Update confirmation summary to reflect changes
                const confirmationSlide = this.quizComponent.querySelector('.pet-quiz-slide--confirmation');
                if (confirmationSlide) {
                  const summaryContainer = confirmationSlide.querySelector('[data-confirmation-summary]');
                  if (summaryContainer) {
                    this.quizComponent.updateConfirmationSummary(summaryContainer);
                  }
                }
                
                // Persist to localStorage
                this.quizComponent.persistToLocalStorage();
                
                // Close drawer
                if (this.hide) {
                  this.hide();
                }
              } catch (error) {
                console.error('Error updating quiz data:', error);
                alert('Failed to update quiz data. Please try again.');
              } finally {
                this.saving = false;
                if (saveBtn) {
                  saveBtn.disabled = false;
                  saveBtn.classList.remove('loading');
                }
              }
            } else {
              // Fallback to original save if not in quiz mode
              if (this._originalSaveAllChanges) {
                return this._originalSaveAllChanges();
              }
            }
          };
          
          // Also listen for drawer close to ensure data is synced even if cancelled
          const handleDrawerClose = () => {
            // If there are unsaved changes when drawer closes, we might want to keep them
            // But for now, we only sync on save
            drawer.removeEventListener('drawer:closed', handleDrawerClose);
          };
          
          drawer.addEventListener('drawer:closed', handleDrawerClose);
          
          drawer.show(petData);
        }
      }

      collectQuizData() {
        // Fields that use .value from metaobjects
        const metaobjectValueFields = ['petBodyType', 'petActivityLevel', 'petPooType', 'petEatsTreats'];
        
        // Collect from all quiz fields
        const quizFields = this.querySelectorAll('[data-quiz-field]');
        quizFields.forEach(field => {
          const fieldName = field.getAttribute('data-quiz-field');
          if (!fieldName) return;
          
          // Skip range inputs for slider-based fields - they contain the index, not the value
          if (metaobjectValueFields.includes(fieldName) && field.type === 'range') {
            return;
          }
          
          // Debug logging for petName collection
          if (fieldName === 'petName' && this.debugMode) {
            console.log('Pet Quiz: Collecting petName', {
              fieldValue: field.value,
              trimmedValue: field.value ? String(field.value).trim() : '',
              fieldVisible: field.offsetParent !== null,
              currentValue: this.quizData.petName
            });
          }
          
          if (field.type === 'checkbox') {
            this.quizData[fieldName] = field.checked;
          } else if (field.type === 'radio') {
            if (field.checked) {
              this.quizData[fieldName] = field.value;
            }
          } else if (field.tagName === 'SELECT') {
            this.quizData[fieldName] = field.value;
          } else if (field.type === 'text' && fieldName === 'petBreed') {
            // For breed search input, get value from hidden input
            // Only update if we have a non-empty value to prevent empty values from overwriting existing data
            const hiddenInput = field.closest('.pet-quiz-slide__breed-search-container')?.querySelector('[name="pet_breed_value"]');
            let breedValue = '';
            
            // Debug logging
            if (this.debugMode) {
              console.log('Pet Quiz: Collecting petBreed', {
                hiddenInputValue: hiddenInput ? hiddenInput.value : 'no hidden input',
                fieldValue: field.value,
                currentValue: this.quizData.petBreed,
                fieldVisible: field.offsetParent !== null
              });
            }
            
            if (hiddenInput && hiddenInput.value && hiddenInput.value.trim() !== '') {
              breedValue = String(hiddenInput.value).trim();
            } else if (field.value && field.value.trim() !== '') {
              // Fallback to text input value if hidden input not found
              const trimmedValue = field.value.trim();
              breedValue = trimmedValue.toLowerCase() === 'unknown' ? 'unknown' : trimmedValue;
            }
            
            // Only update if we have a value
            if (breedValue !== '') {
              this.quizData[fieldName] = breedValue;
              
              // Debug logging
              if (this.debugMode) {
                console.log('Pet Quiz: Updated petBreed to:', breedValue);
              }
            } else if (this.debugMode) {
              console.log('Pet Quiz: petBreed field is empty, preserving existing value:', this.quizData.petBreed);
            }
          } else if (field.type === 'text' && fieldName === 'petBrandFood') {
            // For brand food search input, get value from hidden input
            // Only update if we have a non-empty value to prevent empty values from overwriting existing data
            const hiddenInput = field.closest('.pet-quiz-slide__breed-search-container')?.querySelector('[name="pet_brand_food_value"]');
            let brandValue = '';
            
            // Debug logging
            if (this.debugMode) {
              console.log('Pet Quiz: Collecting petBrandFood', {
                hiddenInputValue: hiddenInput ? hiddenInput.value : 'no hidden input',
                fieldValue: field.value,
                currentValue: this.quizData.petBrandFood,
                fieldVisible: field.offsetParent !== null
              });
            }
            
            if (hiddenInput && hiddenInput.value && hiddenInput.value.trim() !== '') {
              brandValue = String(hiddenInput.value).trim();
            } else if (field.value && field.value.trim() !== '') {
              brandValue = String(field.value).trim();
            }
            
            // Only update if we have a value
            if (brandValue !== '') {
              this.quizData[fieldName] = brandValue;
              
              // Debug logging
              if (this.debugMode) {
                console.log('Pet Quiz: Updated petBrandFood to:', brandValue);
              }
            } else if (this.debugMode) {
              console.log('Pet Quiz: petBrandFood field is empty, preserving existing value:', this.quizData.petBrandFood);
            }
          } else if (field.type === 'hidden') {
            // Skip hidden inputs for petBreed and petBrandFood - handled via text inputs
            if (fieldName === 'petBreed' || fieldName === 'petBrandFood') {
              return;
            }
            // Fields that use .value from metaobjects: single-selection, store plain strings, not JSON
            if (metaobjectValueFields.includes(fieldName)) {
              if (field.value && field.value.trim() !== '') {
                this.quizData[fieldName] = field.value.trim();
                if (this.debugMode) {
                  console.log(`Pet Quiz: Collected ${fieldName}:`, field.value.trim());
                }
              }
              return;
            }
            // Handle JSON values (like allergies and change reasons - multiple selection)
            try {
              const parsed = JSON.parse(field.value || '[]');
              if (Array.isArray(parsed)) {
                this.quizData[fieldName] = parsed;
              } else {
                this.quizData[fieldName] = field.value;
              }
            } catch (e) {
              this.quizData[fieldName] = field.value;
            }
          } else {
            // Safety check: never collect range inputs in the else block
            if (field.type === 'range') {
              if (this.debugMode) {
                console.warn('Pet Quiz: Range input reached else block - this should not happen for', fieldName);
              }
              return;
            }
            
            // For text inputs (like petName), trim the value
            // Only update if field has a non-empty value to prevent empty strings from overwriting existing data
            const trimmedValue = field.value ? String(field.value).trim() : '';
            if (trimmedValue !== '') {
              // Field has a value - update quizData
              this.quizData[fieldName] = trimmedValue;
              
              // Debug logging for petName
              if (fieldName === 'petName' && this.debugMode) {
                console.log('Pet Quiz: Updated petName to:', trimmedValue);
              }
            } else if (fieldName === 'petName' && this.debugMode) {
              // Debug: field is empty, preserving existing value
              console.log('Pet Quiz: petName field is empty, preserving existing value:', this.quizData.petName);
            }
            // If field is empty, don't overwrite existing value in quizData
            // This prevents empty values from inactive/hidden slides from clearing previously collected data
          }
        });
        
        // Also collect from feeding calculator fields (weight, age, activity)
        const catWeight = this.querySelector('[data-cat-weight]');
        if (catWeight && catWeight.value) {
          this.quizData.petWeight = parseFloat(catWeight.value);
        }
        
        const dogWeight = this.querySelector('[data-dog-weight]');
        if (dogWeight && dogWeight.value) {
          this.quizData.petWeight = parseFloat(dogWeight.value);
        }
        
        // Collect date of birth (store as ISO format YYYY-MM-DD for GraphQL)
        const dateOfBirthInput = this.querySelector('[data-quiz-field="petDateOfBirth"]');
        if (dateOfBirthInput && dateOfBirthInput.value) {
          const dateValue = dateOfBirthInput.value; // Format: YYYY-MM-DD
          if (dateValue) {
            try {
              const date = new Date(dateValue);
              if (!isNaN(date.getTime())) {
                // Store as ISO format (YYYY-MM-DD) for GraphQL DateOrDateTime
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                this.quizData.petBirthday = `${year}-${month}-${day}`;
              }
            } catch (e) {
              console.warn('Pet Quiz: Error formatting date of birth:', e);
            }
          }
        }
        
        // Collect activity level from hidden input (now using metaobject tag cloud)
        const activityLevelInput = this.querySelector('[data-quiz-field="petActivityLevel"]');
        if (activityLevelInput && activityLevelInput.value) {
          const activityValue = activityLevelInput.value.trim();
          // Don't set if value is empty, "[]", or starts with "[" (invalid)
          if (activityValue && activityValue !== '[]' && !activityValue.startsWith('[')) {
            // Value should already be in correct format from metaobject: high_activity, moderate_activity, low_activity, sedentary
            this.quizData.petActivityLevel = activityValue;
          }
        }
        
        // Collect treats from tag cloud (new metaobject-based approach)
        const treatsInput = this.querySelector('[data-quiz-field="petEatsTreats"]');
        if (treatsInput) {
          const treatsValue = treatsInput.value.trim();
          // Store the value from metaobject (e.g., "yes", "no", "sometimes")
          if (treatsValue && treatsValue !== '[]' && !treatsValue.startsWith('[')) {
            this.quizData.petEatsTreats = treatsValue;
          } else {
            // Fallback: check for old checkbox format for backward compatibility
            const treatsCheckbox = this.querySelector('[data-treats]');
            if (treatsCheckbox) {
              this.quizData.petEatsTreats = treatsCheckbox.checked ? 'yes' : 'no';
            } else {
              this.quizData.petEatsTreats = '';
            }
          }
        } else {
          // Fallback: check for old checkbox format for backward compatibility
          const treatsCheckbox = this.querySelector('[data-treats]');
          if (treatsCheckbox) {
            this.quizData.petEatsTreats = treatsCheckbox.checked ? 'yes' : 'no';
          }
        }
        
        // Collect pet type from buttons
        const petTypeButton = this.querySelector('[data-pet-type]:checked, [data-pet-type].btn');
        if (petTypeButton) {
          const petType = petTypeButton.getAttribute('data-pet-type') || petTypeButton.dataset.petType;
          if (petType) {
            this.quizData.pet_type = petType.toLowerCase().trim();
            this.quizData.petType = this.quizData.pet_type; // Keep both for compatibility
          }
        }
        
        // Collect food type and ratios from food preference slider
        // Collect food type from hidden input (only if user has made a selection)
        const foodTypeInput = this.querySelector('[name="pet_food_type"]');
        if (foodTypeInput && foodTypeInput.value && foodTypeInput.value.trim() !== '') {
          const foodType = foodTypeInput.value.toLowerCase();
          this.quizData.petFoodType = foodType;
          
          // Set ratios based on food type
          if (foodType === 'wet') {
            this.quizData.wetFoodRatio = 100;
            this.quizData.dryFoodRatio = 0;
            this.quizData.petFoodMixRatio = '100/0';
          } else if (foodType === 'dry') {
            this.quizData.wetFoodRatio = 0;
            this.quizData.dryFoodRatio = 100;
            this.quizData.petFoodMixRatio = '0/100';
          } else if (foodType === 'mix') {
            // For mix, get ratios from slider if available
            const foodTypeSlider = this.querySelector('[name="pet_food_type_slider"]');
            if (foodTypeSlider && foodTypeSlider.value !== undefined) {
              const sliderValue = parseInt(foodTypeSlider.value, 10);
              this.quizData.wetFoodRatio = sliderValue;
              this.quizData.dryFoodRatio = 100 - sliderValue;
              this.quizData.petFoodMixRatio = `${sliderValue}/${100 - sliderValue}`;
            } else {
              // Default to 50/50 if slider not available
              this.quizData.wetFoodRatio = 50;
              this.quizData.dryFoodRatio = 50;
              this.quizData.petFoodMixRatio = '50/50';
            }
          }
        } else {
          // Clear food type data if no selection made
          delete this.quizData.petFoodType;
          delete this.quizData.wetFoodRatio;
          delete this.quizData.dryFoodRatio;
          delete this.quizData.petFoodMixRatio;
        }
      }

      validateSlide(slide) {
        if (!slide) return true;
        
        const requiredFields = slide.querySelectorAll('[required], [data-required="true"]');
        let isValid = true;
        
        requiredFields.forEach(field => {
          // Skip validation if field is disabled (e.g., breed input when "unsure" is checked)
          if (field.disabled) {
            return;
          }
          
          let fieldValid = true;
          
          // Handle tag cloud fields (body type, poo type, allergies, change reason)
          if (field.hasAttribute('data-quiz-field') && field.type === 'hidden') {
            const fieldName = field.getAttribute('data-quiz-field');
            const fieldValue = field.value;
            
            // Check if it's a JSON array (multiple selection) or single value
            if (fieldValue) {
              try {
                const parsed = JSON.parse(fieldValue);
                if (Array.isArray(parsed)) {
                  fieldValid = parsed.length > 0 && parsed[0] !== '';
                  
                  // Special validation for change reason: if "other" is selected, check if other input is filled
                  if (fieldName === 'petDietChangeReason' && fieldValid) {
                    const hasOther = parsed.some(v => String(v).toLowerCase().trim() === 'other');
                    if (hasOther) {
                      const otherInput = slide.querySelector('[data-quiz-field="petDietChangeReasonOther"]');
                      if (!otherInput || !otherInput.value || !otherInput.value.trim()) {
                        fieldValid = false;
                        // Show error on the other input field
                        if (otherInput) {
                          otherInput.classList.add('error');
                          otherInput.setAttribute('aria-invalid', 'true');
                          const errorId = `error-${fieldName}-other`;
                          if (!slide.querySelector(`[data-validation-error="${errorId}"]`)) {
                            const errorDiv = document.createElement('div');
                            errorDiv.className = 'pet-quiz-slide__error-message';
                            errorDiv.setAttribute('data-validation-error', errorId);
                            errorDiv.textContent = 'Please specify your reason';
                            const formField = otherInput.closest('.form-field');
                            if (formField) {
                              formField.appendChild(errorDiv);
                            } else {
                              otherInput.parentNode.appendChild(errorDiv);
                            }
                          }
                        }
                      } else {
                        // Clear error if other input is filled
                        if (otherInput) {
                          otherInput.classList.remove('error');
                          otherInput.removeAttribute('aria-invalid');
                          const errorId = `error-${fieldName}-other`;
                          const errorDiv = slide.querySelector(`[data-validation-error="${errorId}"]`);
                          if (errorDiv) {
                            errorDiv.remove();
                          }
                        }
                      }
                    }
                  }
                } else {
                  fieldValid = fieldValue.trim() !== '';
                }
              } catch (e) {
                // Not JSON, treat as string
                fieldValid = fieldValue.trim() !== '';
              }
            } else {
              fieldValid = false;
            }
            
            // If invalid, show error on the tag cloud container
            if (!fieldValid) {
              isValid = false;
              const tagCloud = field.closest('[data-field-input]');
              if (tagCloud) {
                tagCloud.classList.add('error');
                const errorId = `error-${fieldName}`;
                if (!slide.querySelector(`[data-validation-error="${errorId}"]`)) {
                  const errorDiv = document.createElement('div');
                  errorDiv.className = 'pet-quiz-slide__error-message';
                  errorDiv.setAttribute('data-validation-error', errorId);
                  errorDiv.textContent = 'This field is required';
                  tagCloud.appendChild(errorDiv);
                }
              }
            } else {
              const tagCloud = field.closest('[data-field-input]');
              if (tagCloud) {
                tagCloud.classList.remove('error');
                const errorId = `error-${field.getAttribute('data-quiz-field')}`;
                const errorDiv = slide.querySelector(`[data-validation-error="${errorId}"]`);
                if (errorDiv) {
                  errorDiv.remove();
                }
              }
            }
            return; // Skip default validation for tag cloud fields
          }
          
          if (field.type === 'checkbox') {
            fieldValid = field.checked;
          } else if (field.type === 'radio') {
            const radioGroup = slide.querySelectorAll(`input[type="radio"][name="${field.name}"]`);
            fieldValid = Array.from(radioGroup).some(radio => radio.checked);
          } else if (field.type === 'email') {
            // Email validation - check format
            const emailValue = field.value.trim();
            if (!emailValue) {
              fieldValid = false;
            } else {
              // Email regex pattern (matches HTML5 email pattern)
              const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
              fieldValid = emailPattern.test(emailValue);
            }
          } else if (field.type === 'date' && field.hasAttribute('data-quiz-field') && field.getAttribute('data-quiz-field') === 'petDateOfBirth') {
            // Date of birth validation - check date range
            const dateValue = field.value.trim();
            if (!dateValue) {
              fieldValid = false;
            } else {
              const selectedDate = new Date(dateValue);
              const today = new Date();
              today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
              
              // Calculate minimum date: max(1996-01-01, 30 years ago)
              const currentYear = today.getFullYear();
              const thirtyYearsAgo = currentYear - 30;
              const minYear = Math.max(1996, thirtyYearsAgo);
              const minDate = new Date(minYear, 0, 1); // January 1st of minYear
              
              // Check if date is valid and within range
              if (isNaN(selectedDate.getTime())) {
                fieldValid = false;
              } else {
                // Check if date is not in the future
                if (selectedDate > today) {
                  fieldValid = false;
                } 
                // Check if date is not older than 30 years (or before 1996)
                else if (selectedDate < minDate) {
                  fieldValid = false;
                } else {
                  fieldValid = true;
                }
              }
            }
          } else if (field.type === 'text' && field.hasAttribute('data-quiz-field') && field.getAttribute('data-quiz-field') === 'petBrandFood') {
            // For brand food search input, check the hidden input value first
            const hiddenInput = field.closest('.pet-quiz-slide__breed-search-container')?.querySelector('[name="pet_brand_food_value"]');
            if (hiddenInput && hiddenInput.value && hiddenInput.value.trim() !== '') {
              fieldValid = true;
            } else {
              // Fallback to text input value
              fieldValid = field.value && field.value.trim() !== '';
            }
          } else {
            fieldValid = field.value && field.value.trim() !== '';
          }
          
          if (!fieldValid) {
            isValid = false;
            field.classList.add('error');
            field.setAttribute('aria-invalid', 'true');
            
            // Show error message if not already shown
            const errorId = `error-${field.name || field.id}`;
            if (!slide.querySelector(`[data-validation-error="${errorId}"]`)) {
              const errorDiv = document.createElement('div');
              errorDiv.className = 'pet-quiz-slide__error-message';
              errorDiv.setAttribute('data-validation-error', errorId);
              
              // Custom error messages based on field type
              let errorMessage = 'This field is required';
              if (field.type === 'email' && field.value && field.value.trim() !== '') {
                errorMessage = 'Please enter a valid email address';
              } else if (field.type === 'date' && field.hasAttribute('data-quiz-field') && field.getAttribute('data-quiz-field') === 'petDateOfBirth' && field.value && field.value.trim() !== '') {
                // Date validation error messages
                const dateValue = field.value.trim();
                const selectedDate = new Date(dateValue);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                if (isNaN(selectedDate.getTime())) {
                  errorMessage = 'Please enter a valid date';
                } else if (selectedDate > today) {
                  errorMessage = 'Date cannot be in the future';
                } else {
                  // Calculate minimum date for error message
                  const currentYear = today.getFullYear();
                  const thirtyYearsAgo = currentYear - 30;
                  const minYear = Math.max(1996, thirtyYearsAgo);
                  errorMessage = `Date must be after ${minYear}-01-01 and not more than 30 years ago`;
                }
              }
              
              errorDiv.textContent = errorMessage;
              
              const fieldName = field.getAttribute('data-quiz-field') || field.name;
              
              // For gender radio buttons (cat_gender, dog_gender), place error below the field-wrapper container
              if (field.type === 'radio' && (field.name === 'cat_gender' || field.name === 'dog_gender')) {
                const fieldWrapper = field.closest('.pet-quiz-slide__field-wrapper');
                if (fieldWrapper) {
                  // Append error to the field-wrapper container (appears below the form-field)
                  fieldWrapper.appendChild(errorDiv);
                } else {
                  const formField = field.closest('.form-field');
                  if (formField && formField.parentNode) {
                    formField.parentNode.appendChild(errorDiv);
                  } else {
                    field.parentNode.appendChild(errorDiv);
                  }
                }
              } else if (fieldName === 'petActivityLevel' || fieldName === 'petBodyType' || fieldName === 'petEatsTreats') {
                // For activity level, body type, and treats tag clouds, place error below the tag cloud container
                const tagCloudContainer = field.closest('.pet-quiz-slide__option-tag-cloud');
                if (tagCloudContainer) {
                  tagCloudContainer.appendChild(errorDiv);
                } else {
                  field.parentNode.appendChild(errorDiv);
                }
              } else if (fieldName === 'petWeight' || field.hasAttribute('data-cat-weight') || field.hasAttribute('data-dog-weight')) {
                // For weight fields, place error in the field-wrapper container (not form-field)
                const fieldWrapper = field.closest('.pet-quiz-slide__field-wrapper');
                if (fieldWrapper) {
                  fieldWrapper.appendChild(errorDiv);
                } else {
                  // Fallback: try form-field
                  const formField = field.closest('.form-field');
                  if (formField) {
                    formField.appendChild(errorDiv);
                  } else {
                    field.parentNode.appendChild(errorDiv);
                  }
                }
              } else {
                // For other fields, place error inside the form-field container
                const formField = field.closest('.form-field');
                if (formField) {
                  formField.appendChild(errorDiv);
                } else {
                  // Fallback: try field-wrapper
                  const fieldWrapper = field.closest('.pet-quiz-slide__field-wrapper');
                  if (fieldWrapper) {
                    fieldWrapper.appendChild(errorDiv);
                  } else {
                    field.parentNode.appendChild(errorDiv);
                  }
                }
              }
            }
          } else {
            field.classList.remove('error');
            field.removeAttribute('aria-invalid');
            const errorId = `error-${field.name || field.id}`;
            const errorDiv = slide.querySelector(`[data-validation-error="${errorId}"]`);
            if (errorDiv) {
              errorDiv.remove();
            }
          }
        });
        
        return isValid;
      }

      validateEmailField(field) {
        if (!field || field.type !== 'email') return;
        
        const emailValue = field.value.trim();
        const slide = field.closest('.pet-quiz-slide, .feeding-calculator-slide');
        if (!slide) return;
        
        const errorId = `error-${field.name || field.id}`;
        const existingError = slide.querySelector(`[data-validation-error="${errorId}"]`);
        
        // Remove existing error first
        if (existingError) {
          existingError.remove();
        }
        field.classList.remove('error');
        field.removeAttribute('aria-invalid');
        
        // Validate email format
        if (emailValue) {
          const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          if (!emailPattern.test(emailValue)) {
            field.classList.add('error');
            field.setAttribute('aria-invalid', 'true');
            
            // Show error message
            const errorDiv = document.createElement('div');
            errorDiv.className = 'pet-quiz-slide__error-message';
            errorDiv.setAttribute('data-validation-error', errorId);
            errorDiv.textContent = 'Please enter a valid email address';
            
            const formField = field.closest('.form-field');
            if (formField) {
              formField.appendChild(errorDiv);
            } else {
              field.parentNode.appendChild(errorDiv);
            }
          }
        }
      }

      validateDateField(field) {
        if (!field || field.type !== 'date' || !field.hasAttribute('data-quiz-field') || field.getAttribute('data-quiz-field') !== 'petDateOfBirth') return;
        
        const dateValue = field.value.trim();
        const slide = field.closest('.pet-quiz-slide, .feeding-calculator-slide');
        if (!slide) return;
        
        const errorId = `error-${field.name || field.id}`;
        const existingError = slide.querySelector(`[data-validation-error="${errorId}"]`);
        
        // Remove existing error first
        if (existingError) {
          existingError.remove();
        }
        field.classList.remove('error');
        field.removeAttribute('aria-invalid');
        
        // Validate date range
        if (dateValue) {
          const selectedDate = new Date(dateValue);
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
          
          // Calculate minimum date: max(1996-01-01, 30 years ago)
          const currentYear = today.getFullYear();
          const thirtyYearsAgo = currentYear - 30;
          const minYear = Math.max(1996, thirtyYearsAgo);
          const minDate = new Date(minYear, 0, 1); // January 1st of minYear
          
          let isValid = true;
          let errorMessage = '';
          
          // Check if date is valid
          if (isNaN(selectedDate.getTime())) {
            isValid = false;
            errorMessage = 'Please enter a valid date';
          } 
          // Check if date is in the future
          else if (selectedDate > today) {
            isValid = false;
            errorMessage = 'Date cannot be in the future';
          } 
          // Check if date is older than 30 years (or before 1996)
          else if (selectedDate < minDate) {
            isValid = false;
            errorMessage = `Date must be after ${minYear}-01-01 and not more than 30 years ago`;
          }
          
          if (!isValid) {
            field.classList.add('error');
            field.setAttribute('aria-invalid', 'true');
            
            // Show error message
            const errorDiv = document.createElement('div');
            errorDiv.className = 'pet-quiz-slide__error-message';
            errorDiv.setAttribute('data-validation-error', errorId);
            errorDiv.textContent = errorMessage;
            
            const formField = field.closest('.form-field');
            if (formField) {
              formField.appendChild(errorDiv);
            } else {
              field.parentNode.appendChild(errorDiv);
            }
          }
        }
      }

      ensureDefaultSelections(slide) {
        if (!slide) return;
        
        // Ensure age selects have default values
        const ageSelects = slide.querySelectorAll('select[name="cat_age"], select[name="dog_age"]');
        ageSelects.forEach(select => {
          if (!select.value && select.options.length > 0) {
            select.selectedIndex = 0;
            select.value = select.options[0].value;
          }
        });
        
        // Ensure checkboxes are initialized in quizData based on their checked state
        const checkboxes = slide.querySelectorAll('input[type="checkbox"][data-quiz-field]');
        checkboxes.forEach(checkbox => {
          const fieldName = checkbox.getAttribute('data-quiz-field');
          if (fieldName) {
            // Initialize quizData with the current checked state
            // If checkbox has 'checked' attribute or is checked, set to true
            // Otherwise, only initialize if not already set (to avoid overwriting user changes)
            if (checkbox.hasAttribute('checked') || checkbox.checked) {
              this.quizData[fieldName] = true;
              checkbox.checked = true; // Ensure visually checked
              
              if (this.debugMode) {
                console.log(`Pet Quiz: Initialized ${fieldName} checkbox to checked state`);
              }
            } else if (this.quizData[fieldName] === undefined) {
              // Only set to false if not already set (preserve user changes)
              this.quizData[fieldName] = false;
              
              if (this.debugMode) {
                console.log(`Pet Quiz: Initialized ${fieldName} checkbox to unchecked state`);
              }
            }
          }
        });
      }

      addToHistory(slide, direction) {
        const slideId = slide.id;
        const slideType = slide.dataset.slideType || 'unknown';
        
        if (direction === 'forward' || direction === 'initial') {
          this.navigationHistory.push({ slideId, slideType, timestamp: Date.now() });
        }
        // Note: 'back' direction was never used, removed for simplicity
      }

      formatQuizData() {
        // Transform collected data to Gadget format - send all data directly to quizResponse
        // Flatten pet data into top-level fields (quizResponse doesn't have a pets array)
        
        // Convert petAllergies array to JSON string
        let petAllergiesString = '';
        if (Array.isArray(this.quizData.petAllergies) && this.quizData.petAllergies.length > 0) {
          petAllergiesString = JSON.stringify(this.quizData.petAllergies);
        }
        
        // Only set ratios if petFoodType is explicitly set
        let petFoodType = this.quizData.petFoodType || '';
        let wetFoodRatio = this.quizData.wetFoodRatio;
        let dryFoodRatio = this.quizData.dryFoodRatio;
        
        // Fallback: parse from petFoodMixRatio if ratios not set directly
        if (wetFoodRatio === undefined || dryFoodRatio === undefined) {
          if (this.quizData.petFoodMixRatio) {
            const parts = this.quizData.petFoodMixRatio.split('/');
            if (parts.length === 2) {
              wetFoodRatio = parseInt(parts[0], 10);
              dryFoodRatio = parseInt(parts[1], 10);
            }
          }
        }
        
        // Only set default ratios if petFoodType is set
        if (petFoodType) {
          if (wetFoodRatio === undefined || isNaN(wetFoodRatio)) {
            if (petFoodType === 'wet') {
              wetFoodRatio = 100;
              dryFoodRatio = 0;
            } else if (petFoodType === 'dry') {
              wetFoodRatio = 0;
              dryFoodRatio = 100;
            } else {
              wetFoodRatio = 50;
              dryFoodRatio = 50;
            }
          }
          if (dryFoodRatio === undefined || isNaN(dryFoodRatio)) {
            if (petFoodType === 'wet') {
              dryFoodRatio = 0;
            } else if (petFoodType === 'dry') {
              dryFoodRatio = 100;
            } else {
              dryFoodRatio = 100 - (wetFoodRatio || 50);
            }
          }
        }
        // Don't infer petFoodType from ratios - user must explicitly select a food type
        
        // Ensure petBreed is a string (not array)
        let petBreed = '';
        if (this.quizData.petBreed) {
          if (Array.isArray(this.quizData.petBreed)) {
            petBreed = this.quizData.petBreed.join(', ');
          } else {
            petBreed = String(this.quizData.petBreed);
          }
        }
        
        // Ensure petBrandFood is a string (not array)
        let petBrandFood = '';
        if (this.quizData.petBrandFood) {
          if (Array.isArray(this.quizData.petBrandFood)) {
            petBrandFood = this.quizData.petBrandFood.join(', ');
          } else {
            petBrandFood = String(this.quizData.petBrandFood);
          }
        }
        
         // Normalize pet gender to lowercase "male" or "female"
         let petSex = '';
         if (this.quizData.petGender) {
           const normalizedGender = String(this.quizData.petGender).toLowerCase().trim();
           // Only include if it's "male" or "female" (exclude "unknown" or empty)
           if (normalizedGender === 'male' || normalizedGender === 'female') {
             petSex = normalizedGender;
           }
         }
         
         // Ensure termsChecked is a boolean
         const termsChecked = typeof this.quizData.termsChecked === 'boolean' 
           ? this.quizData.termsChecked 
           : Boolean(this.quizData.termsChecked);
         
         // Format petDietChangeReason: handle array and "other" case
         let petDietChangeReasonString = '';
         if (Array.isArray(this.quizData.petDietChangeReason) && this.quizData.petDietChangeReason.length > 0) {
           // Handle "other" case - replace "other" with the actual text if provided
           const changeReasonArray = [...this.quizData.petDietChangeReason];
           const otherIndex = changeReasonArray.findIndex(v => String(v).toLowerCase().trim() === 'other');
           if (otherIndex >= 0 && this.quizData.petDietChangeReasonOther && this.quizData.petDietChangeReasonOther.trim()) {
             changeReasonArray[otherIndex] = this.quizData.petDietChangeReasonOther.trim();
           }
           // Convert array to comma-separated string
           petDietChangeReasonString = changeReasonArray.join(', ');
         } else if (this.quizData.petDietChangeReason && typeof this.quizData.petDietChangeReason === 'string') {
           petDietChangeReasonString = this.quizData.petDietChangeReason;
         }
         
         // Format petBodyType: handle array (convert to string or empty string)
         let petBodyTypeString = '';
         if (Array.isArray(this.quizData.petBodyType) && this.quizData.petBodyType.length > 0) {
           // Convert array to comma-separated string (or single value if only one)
           petBodyTypeString = this.quizData.petBodyType.join(', ');
         } else if (this.quizData.petBodyType && typeof this.quizData.petBodyType === 'string' && this.quizData.petBodyType.trim() !== '') {
           petBodyTypeString = this.quizData.petBodyType.trim();
         }
         
         // Debug logging for petBodyType formatting
         if (this.debugMode) {
           console.log('Pet Quiz: Formatting petBodyType', {
             rawValue: this.quizData.petBodyType,
             formattedValue: petBodyTypeString,
             type: typeof this.quizData.petBodyType,
             isArray: Array.isArray(this.quizData.petBodyType)
           });
         }
         
         // Build the data object
         const quizData = {
          shop: { _link: this.shopId },
          email: this.quizData.email || '',
          userName: this.quizData.userName || '',
          termsChecked: termsChecked,
          petName: this.quizData.petName || '',
          petType: this.quizData.petType || this.quizData.pet_type || '',
          petGender: petSex, // API expects petSex, normalized to "male" or "female"
          petBreed: petBreed,
          isPetRescue: this.quizData.isPetRescue || false,
          petWeight: this.quizData.petWeight || 0,
          petActivityLevel: (() => {
            const activity = this.quizData.petActivityLevel || '';
            // Validate activity level - must be one of the exact values from metaobject
            const validValues = ['high_activity', 'moderate_activity', 'low_activity', 'sedentary'];
            if (activity && activity !== '[]' && !activity.startsWith('[')) {
              const normalized = activity.toLowerCase().trim();
              // Check if it matches one of the valid values (case-insensitive)
              const matchingValue = validValues.find(v => v.toLowerCase() === normalized);
              if (matchingValue) {
                return matchingValue; // Return exact format
              }
              // If not matching, return empty string (invalid value)
              console.warn('Pet Quiz: Invalid activity level value:', activity);
              return '';
            }
            return '';
          })(),
          petFoodType: petFoodType,
          wetFoodRatio: wetFoodRatio,
          dryFoodRatio: dryFoodRatio,
          petBrandFood: petBrandFood,
          petEatsTreats: this.quizData.petEatsTreats || '',
          petPooType: this.quizData.petPooType || '',
          petDietChangeReason: petDietChangeReasonString
        };
        
        // Include petIndex if available
        if (this.quizData.petIndex !== undefined && this.quizData.petIndex !== null) {
          quizData.petIndex = this.quizData.petIndex;
        }
        
        // Always include petBodyType (even if empty, backend may need it)
        quizData.petBodyType = petBodyTypeString || null;
        
        // Format and include petBirthday (ISO date format YYYY-MM-DD)
        const dateOfBirthInput = this.querySelector('[data-quiz-field="petDateOfBirth"]');
        if (dateOfBirthInput && dateOfBirthInput.value) {
          const dateValue = dateOfBirthInput.value; // Format: YYYY-MM-DD
          if (dateValue) {
            try {
              const date = new Date(dateValue);
              if (!isNaN(date.getTime())) {
                // Format as ISO date string (YYYY-MM-DD) for GraphQL DateOrDateTime
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                quizData.petBirthday = `${year}-${month}-${day}`;
              }
            } catch (e) {
              console.warn('Pet Quiz: Error formatting date of birth:', e);
            }
          }
        } else {
          // Fallback: use petDateOfBirth when petBirthday not set (API/dataset compatibility)
          const birthdateValue = this.quizData.petBirthday || this.quizData.petDateOfBirth;
          if (birthdateValue && birthdateValue.trim() !== '') {
          // Try to parse and reformat if it's in MM-DD-YY format
          try {
            const parts = birthdateValue.split('-');
            if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 2) {
              // MM-DD-YY format
              const month = parts[0];
              const day = parts[1];
              const year = '20' + parts[2]; // Assume 2000s
              const date = new Date(`${year}-${month}-${day}`);
              if (!isNaN(date.getTime())) {
                quizData.petBirthday = `${year}-${month}-${day}`;
              }
            } else {
              // Try parsing as-is
              const date = new Date(birthdateValue);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                quizData.petBirthday = `${year}-${month}-${day}`;
              }
            }
          } catch (e) {
            console.warn('Pet Quiz: Error parsing existing date of birth:', e);
          }
          }
        }
        
        // Only include petAllergies if there are any
        if (petAllergiesString) {
          quizData.petAllergies = petAllergiesString;
        }
        
        return quizData;
      }

      async getPetIndex(email) {
        // If petIndex is already set, bypass the call
        if (this.quizData.petIndex !== undefined && this.quizData.petIndex !== null && this.quizData.petIndex !== '') {
          if (this.debugMode) {
            console.info('Pet Quiz: petIndex already set to', this.quizData.petIndex, '- bypassing getPetIndex call');
          }
          return;
        }

        if (!email || !email.trim()) {
          console.warn('Pet Quiz: Cannot get pet index - email is missing');
          return;
        }

        if (!this.shopId) {
          console.warn('Pet Quiz: Cannot get pet index - shopId is missing');
          return;
        }

        if (!this.gadgetInitialized && !window.api) {
          console.warn('Pet Quiz: Gadget API not initialized, cannot get pet index');
          return;
        }

        if (!window.api) {
          console.warn('Pet Quiz: window.api not available');
          return;
        }

        try {
          // Call getPetIndex function from Gadget
          // Custom actions are exposed directly on window.api (like getSkioCustomerID)
          if (!window.api.getPetIndex || typeof window.api.getPetIndex !== 'function') {
            console.warn('Pet Quiz: getPetIndex action not found on window.api');
            return;
          }

          const result = await window.api.getPetIndex({ 
            email: email.trim(),
            shopId: this.shopId
          });
          
          // Handle different response formats
          let petIndex;
          if (result !== undefined && result !== null) {
            // If result is an object with petIndex property
            if (typeof result === 'object' && 'petIndex' in result) {
              petIndex = result.petIndex;
            } 
            // If result is a number directly
            else if (typeof result === 'number') {
              petIndex = result;
            }
            // If result has a data property
            else if (result.data && result.data.petIndex !== undefined) {
              petIndex = result.data.petIndex;
            }
            else {
              petIndex = result;
            }
          }

          if (petIndex !== undefined && petIndex !== null) {
            // Increment petIndex by 1 (if API returns 2, we store 3)
            const incrementedPetIndex = typeof petIndex === 'number' ? petIndex + 1 : parseInt(petIndex, 10) + 1;
            this.quizData.petIndex = incrementedPetIndex;
            this.persistToLocalStorage();
            
            if (this.debugMode) {
              console.info('Pet Quiz: Retrieved pet index:', petIndex, 'incremented to:', incrementedPetIndex, 'for email:', email);
            }
          }
        } catch (error) {
          console.error('Pet Quiz: Error getting pet index:', error);
          // Don't block navigation if getPetIndex fails
        }
      }

      async submitToGadget(redirectUrl) {
        if (!this.gadgetInitialized && !window.api) {
          console.error('Gadget API not initialized');
          this.showSubmissionError('Gadget API not initialized. Please refresh the page.');
          return;
        }
        
        if (!window.api || !window.api.quizResponse) {
          console.error('quizResponse model not available');
          this.showSubmissionError('Quiz submission service not available. Please try again later.');
          return;
        }
        
        if (!redirectUrl) {
          console.error('Redirect URL not provided');
          this.showSubmissionError('Redirect URL is not configured. Please contact support.');
          return;
        }
        
        try {
          const quizData = this.formatQuizData();
          
          if (this.debugMode || this.env === 'development') { 
            console.info('=== Pet Quiz Submission ===');
            console.info('Raw quizData.petBodyType:', this.quizData.petBodyType);
            console.info('Submitting quiz data to Gadget:', quizData);
            console.info('Formatted quiz data (JSON):', JSON.stringify(quizData, null, 2));
          }
          
          const response = await window.api.quizResponse.create(quizData);
          
          if (this.debugMode || this.env === 'development') {
            console.info('Quiz submitted successfully:', response);
          }
          
          // Extract response ID (handle different possible response structures)
          const responseId = response?.id || response?.record?.id || response?.data?.id;
          
          if (!responseId) {
            console.error('Response ID not found in API response:', response);
            this.showSubmissionError('Quiz submitted but response ID not found. Please contact support.');
            return;
          }
          
          // Build redirect URL with quiz ID as query parameter
          const separator = redirectUrl.includes('?') ? '&' : '?';
          const redirectUrlWithId = `${redirectUrl}${separator}quizId=${encodeURIComponent(responseId)}`;
          
          // Clear localStorage before redirect
          this.resetQuiz();
          
          // Redirect to results page
          window.location.href = redirectUrlWithId;
          
        } catch (error) {
          console.error('Error submitting quiz to Gadget:', error);
          this.showSubmissionError(error.message || 'Failed to submit quiz. Please try again.');
        }
      }

      showSubmissionError(errorText) {
        const confirmationSlide = this.slides[this.currentSlideIndex];
        if (!confirmationSlide || !confirmationSlide.classList.contains('pet-quiz-slide--confirmation')) {
          // If not on confirmation slide, try to find it
          const slide = this.slides.find(s => s.classList.contains('pet-quiz-slide--confirmation'));
          if (!slide) {
            console.error('Confirmation slide not found for error display');
            return;
          }
          return this.showSubmissionErrorOnSlide(slide, errorText);
        }
        
        this.showSubmissionErrorOnSlide(confirmationSlide, errorText);
      }

      showSubmissionErrorOnSlide(slide, errorText) {
        const loadingState = slide.querySelector('[data-loading-state]');
        const errorMessage = slide.querySelector('[data-error-message]');
        const submitButton = slide.querySelector('[data-submit-quiz]');
        
        if (loadingState) loadingState.hidden = true;
        if (errorMessage) {
          errorMessage.hidden = false;
          const errorTextEl = errorMessage.querySelector('p');
          if (errorTextEl) {
            errorTextEl.textContent = errorText;
          }
        }
        if (submitButton) submitButton.disabled = false;
      }

      resetQuiz() {
        this.quizData = {};
        this.navigationHistory = [];
        this.currentSlideIndex = 0;
        
        if (typeof Storage !== 'undefined') {
          localStorage.removeItem(this.storageKey);
        }
        
        // Reset all form fields
        this.slides.forEach(slide => {
          const inputs = slide.querySelectorAll('input, select, textarea');
          inputs.forEach(input => {
            if (input.type === 'checkbox' || input.type === 'radio') {
              input.checked = false;
            } else {
              input.value = '';
            }
          });
        });
      }

      /**
       * Check if dummy data mode is enabled
       * Requires: debug mode enabled (no URL parameter needed)
       */
      isDummyDataModeEnabled() {
        // Must be in debug mode
        return this.debugMode === true;
      }

      /**
       * Setup dummy data button (located in section file, outside pet-quiz element)
       */
      setupDummyDataButton() {
        if (!this.isDummyDataModeEnabled()) return;
        
        // Find the dummy data button in the parent container (pet-quiz-main)
        const petQuizMain = this.closest('.pet-quiz-main');
        if (!petQuizMain) {
          // Retry if parent container not ready yet
          setTimeout(() => this.setupDummyDataButton(), 100);
          return;
        }

        const dummyButton = petQuizMain.querySelector('[data-dummy-data-button]');
        if (!dummyButton) return;

        // Show the button (it's always visible when debug mode + URL param are enabled)
        dummyButton.hidden = false;
        
        // Add click handler (only once)
        if (!dummyButton.dataset.handlerAttached) {
          dummyButton.dataset.handlerAttached = 'true';
          
          dummyButton.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            if (dummyButton.disabled) return;
            
            dummyButton.disabled = true;
            const originalText = dummyButton.textContent;
            dummyButton.textContent = 'Sending...';
            
            try {
              const response = await this.submitDummyData();
              
              // If submitDummyData returns a response (no redirect URL configured), show success message
              if (response) {
                dummyButton.textContent = 'Sent!';
                setTimeout(() => {
                  dummyButton.textContent = originalText;
                  dummyButton.disabled = false;
                }, 2000);
              } else {
                // If redirect URL is configured, submitDummyData should have redirected
                // If we reach here, redirect might have failed - re-enable button after a delay
                setTimeout(() => {
                  if (dummyButton.disabled) {
                    console.warn('Redirect may have failed, re-enabling button');
                    dummyButton.textContent = originalText;
                    dummyButton.disabled = false;
                  }
                }, 2000);
              }
            } catch (error) {
              console.error('Error submitting dummy data:', error);
              dummyButton.textContent = 'Error - Try Again';
              dummyButton.disabled = false;
            }
          });
        }
      }

      /**
       * Generate dummy quiz data matching the expected format
       */
      generateDummyData() {
        const now = new Date();
        const createdAt = now.toISOString();
        const updatedAt = now.toISOString();
        
        // Generate a random date of birth (between 1-15 years ago)
        const yearsAgo = Math.floor(Math.random() * 15) + 1;
        const birthDate = new Date(now.getFullYear() - yearsAgo, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1);
        const petBirthday = `${birthDate.getFullYear()}-${String(birthDate.getMonth() + 1).padStart(2, '0')}-${String(birthDate.getDate()).padStart(2, '0')}`;
        
        return {
          shop: { _link: this.shopId },
          email: `ocontis.web@gmail.com`,
          userName: 'Test User',
          termsChecked: true,
          petName: 'Test Pet',
          petType: Math.random() > 0.5 ? 'dog' : 'cat',
          petGender: Math.random() > 0.5 ? 'male' : 'female',
          petBreed: Math.random() > 0.5 ? 'Labrador Retriever' : 'Persian',
          isPetRescue: Math.random() > 0.5,
          petWeight: Math.floor(Math.random() * 30) + 5, // 5-35 kg
          petActivityLevel: 'moderate_activity',
          petBodyType: 'medium',
          petFoodType: Math.random() > 0.5 ? 'wet' : 'dry',
          wetFoodRatio: Math.random() > 0.5 ? 50 : 0,
          dryFoodRatio: Math.random() > 0.5 ? 50 : 100,
          petBrandFood: 'Test Brand',
          petEatsTreats: Math.random() > 0.5,
          petPooType: 'Perfect Poop!',
          petDietChangeReason: 'Testing dummy data submission',
          petBirthday: petBirthday,
          petAllergies: '',
          petIndex: 1
        };
      }

      /**
       * Submit dummy data to backend
       */
      async submitDummyData() {
        if (!this.gadgetInitialized && !window.api) {
          console.error('Gadget API not initialized');
          throw new Error('Gadget API not initialized. Please refresh the page.');
        }
        
        if (!window.api || !window.api.quizResponse) {
          console.error('quizResponse model not available');
          throw new Error('Quiz submission service not available. Please try again later.');
        }
        
        // Get redirect URL from confirmation slide (same as regular submission)
        let redirectUrl = null;
        
        try {
          const confirmationSlide = this.slides.find(s => s.classList.contains('pet-quiz-slide--confirmation'));
          if (confirmationSlide) {
            const submitButton = confirmationSlide.querySelector('[data-submit-quiz]');
            if (submitButton && submitButton.dataset.redirectUrl) {
              redirectUrl = submitButton.dataset.redirectUrl;
            }
          }
        } catch (urlError) {
          console.warn('Error getting redirect URL:', urlError);
        }
        
        if (!redirectUrl) {
          console.warn('Redirect URL not found in confirmation slide. Dummy data will be submitted but no redirect will occur.');
        }
        
        try {
          const dummyData = this.generateDummyData();
          const response = await window.api.quizResponse.create(dummyData);
          
          console.info('=== Quiz Response from Backend (Dummy Data) ===');
          console.info('Quiz response:', JSON.stringify(response, null, 2));
          
          // Extract response ID (handle different possible response structures)
          const responseId = response?.id || response?.record?.id || response?.data?.id;
          
          if (this.debugMode || this.env === 'development') {
            console.info('Dummy data response ID extracted:', responseId);
          }
          
          if (!responseId) {
            console.warn('Response ID not found in response:', response);
            // If no redirect URL, just return the response (button will show "Sent!")
            if (!redirectUrl) {
              return response;
            }
            // If redirect URL is configured but no ID, can't redirect - return response anyway
            console.error('Response ID not found but redirect URL is configured. Cannot redirect.');
            return response;
          }
          
          // If redirect URL is configured, redirect like regular submission
          if (redirectUrl) {
            // Build redirect URL with quiz ID as query parameter
            // Ensure URL is absolute if it's relative
            let finalRedirectUrl = redirectUrl;
            if (redirectUrl.startsWith('/')) {
              // Relative URL - make it absolute
              finalRedirectUrl = window.location.origin + redirectUrl;
            } else if (!redirectUrl.startsWith('http://') && !redirectUrl.startsWith('https://')) {
              // If it doesn't start with http/https and isn't relative, assume it's relative
              finalRedirectUrl = window.location.origin + '/' + redirectUrl.replace(/^\//, '');
            }
            
            const separator = finalRedirectUrl.includes('?') ? '&' : '?';
            const redirectUrlWithId = `${finalRedirectUrl}${separator}quizId=${encodeURIComponent(responseId)}`;
            
            console.info('Dummy data redirect URL:', redirectUrl);
            console.info('Dummy data redirect URL with ID:', redirectUrlWithId);
            
            // Clear localStorage before redirect
            try {
              this.resetQuiz();
            } catch (resetError) {
              console.warn('Error resetting quiz (non-critical):', resetError);
            }
            
            // Redirect to results page
            console.info('Redirecting dummy data to:', redirectUrlWithId);
            
            // Use location.replace to avoid adding to browser history
            // This will navigate away, so the button handler won't execute success code
            window.location.replace(redirectUrlWithId);
            
            // Fallback: if redirect doesn't happen within 1 second, force it
            setTimeout(() => {
              if (window.location.href !== redirectUrlWithId) {
                console.warn('Dummy data redirect did not occur, forcing navigation');
                window.location.href = redirectUrlWithId;
              }
            }, 1000);
            
            // Return undefined since we're redirecting
            // The button handler will not execute success code because page navigates
            return;
          } else {
            // No redirect URL configured, just return response
            console.info('No redirect URL configured for dummy data. Response ID:', responseId);
            return response;
          }
        } catch (error) {
          console.error('Error submitting dummy data to Gadget:', error);
          throw error;
        }
      }

      persistToLocalStorage() {
        if (typeof Storage === 'undefined') return;
        
        try {
          const state = {
            quizData: this.quizData,
            navigationHistory: this.navigationHistory,
            currentSlideIndex: this.currentSlideIndex
          };
          
          // Debug logging for petName
          if (this.debugMode && 'petName' in this.quizData) {
            console.log('Pet Quiz: Persisting to localStorage', {
              petName: this.quizData.petName,
              petNameType: typeof this.quizData.petName,
              petNameLength: this.quizData.petName ? this.quizData.petName.length : 0
            });
          }
          
          localStorage.setItem(this.storageKey, JSON.stringify(state));
        } catch (e) {
          console.warn('Failed to save quiz state to localStorage:', e);
        }
      }

      restoreFromLocalStorage() {
        if (typeof Storage === 'undefined') return;
        
        try {
          const saved = localStorage.getItem(this.storageKey);
          if (!saved) return;
          
          const state = JSON.parse(saved);
          this.quizData = state.quizData || {};
          this.navigationHistory = state.navigationHistory || [];
          this.currentSlideIndex = state.currentSlideIndex || 0;
          
          // Clear food type data if it was set by default (not user-selected)
          // Check if hidden input exists and has no value - means it was a default
          const foodTypeInput = this.querySelector('[name="pet_food_type"]');
          if (foodTypeInput && (!foodTypeInput.value || foodTypeInput.value.trim() === '')) {
            // If quizData has petFoodType but input is empty, it was likely a default value
            // Only clear if we're on a fresh start (no navigation history means first load)
            if (this.navigationHistory.length === 0 && this.quizData.petFoodType === 'mix' && 
                this.quizData.wetFoodRatio === 50 && this.quizData.dryFoodRatio === 50) {
              delete this.quizData.petFoodType;
              delete this.quizData.wetFoodRatio;
              delete this.quizData.dryFoodRatio;
              delete this.quizData.petFoodMixRatio;
            }
          }
          
          this.restoreFormData();
        } catch (e) {
          console.warn('Failed to restore quiz state from localStorage:', e);
        }
      }

      restoreFormData() {
        // Restore form field values
        const metaobjectValueFields = ['petBodyType', 'petActivityLevel', 'petPooType', 'petEatsTreats'];
        const fieldNameMap = {
          petBodyType: '[name="pet_body_type"]',
          petPooType: '[name="pet_poo_type"]'
        };
        
        Object.keys(this.quizData).forEach(key => {
          // For slider-based metaobject fields, target hidden input directly
          let field = fieldNameMap[key] 
            ? this.querySelector(fieldNameMap[key])
            : this.querySelector(`[data-quiz-field="${key}"]`);
          
          if (!field) return;
          
          if (field.type === 'checkbox') {
            field.checked = this.quizData[key] === true;
          } else if (field.type === 'radio') {
            const radio = this.querySelector(`[data-quiz-field="${key}"][value="${this.quizData[key]}"]`);
            if (radio) radio.checked = true;
          } else if (field.tagName === 'SELECT') {
            field.value = this.quizData[key];
          } else if (field.type === 'hidden' && Array.isArray(this.quizData[key])) {
            field.value = JSON.stringify(this.quizData[key]);
          } else {
            field.value = this.quizData[key];
          }
        });
      }

      escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }
    }
  );
}
